import React from "react";
import api from "@/lib/api";
import ReactMarkdown from "react-markdown";
import DOMPurify from "dompurify";
import remarkGfm from "remark-gfm";
import {
  X, Gear, Star, Trash, Lightning, Calendar,
  ToggleLeft, ToggleRight, WarningCircle, Sparkle,
  CheckCircle, PaperPlaneRight, Robot, User as UserIcon, Paperclip,
  Plus, CaretDown,
} from "@phosphor-icons/react";
import { useErp } from "@/context/ErpContext";
import WizardFields from "@/components/erp/WizardFields";
import RFPAgentModal from "@/components/erp/RFPAgentModal";
import InvoiceAgeingModal from "@/components/erp/InvoiceAgeingModal"
import SupplierAgeingModal from "@/components/erp/SupplierAgeingModal";
// FIX (merge): this import + the early-return route below were present in the
// old/working AgentRunModal.jsx but missing from the newer local version —
// that's why supplier-ageing stopped working. Restored as-is; nothing else
// in this file was changed.
import PdfInvoiceReview from "@/components/erp/PdfInvoiceReview"
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const CHAT_ONLY_PATTERNS = [
  "account-overview",
  "account_overview",
  "finance-variance",
  "finance-account-variance",
  "finance-future",
  "future-insight",
  "finance-narrative",
  "finance-reporting-narrative",
];
function buildFieldContext(fieldValues, schemaFields) {
  if (!schemaFields?.length) return "";
  return schemaFields
    .map((f) => {
      const raw = fieldValues[f.name];
      if (!raw) return null;
      if (f.type === "date") return `${f.label}: ${formatDateForContext(raw)}`;
      const label = fieldValues[`${f.name}__label`];
      return `${f.label}: ${label || raw}`;
    })
    .filter(Boolean)
    .join(", ");
}

function isChatOnlyAgent(key) {
  if (!key) return false;
  return CHAT_ONLY_PATTERNS.some((p) => key.includes(p));
}

function timeUntil(iso) {
  if (!iso) return "—";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "due now";
  const m = Math.floor(ms / 60000);
  if (m < 60) return `in ${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) return `in ${h}h ${m % 60}m`;
  return `in ${Math.floor(h / 24)}d`;
}

function describeSchedule(s) {
  const hh = String(s.hour_utc).padStart(2, "0");
  if (s.cadence === "daily") return `Daily · ${hh}:00 UTC`;
  if (s.cadence === "weekly") return `Weekly · ${DOW[s.day_of_week] || "Mon"} · ${hh}:00 UTC`;
  return `Monthly · day ${s.day_of_month} · ${hh}:00 UTC`;
}

/* ── Receipt attachment upload ── */
function ReceiptAttachmentUpload({ fieldUi, sessionId, onUploaded }) {
  const fileInputRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);
  const [uploadStatus, setUploadStatus] = React.useState(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploading(true);
    setUploadStatus(null);
    try {
      const sid = sessionId;
      if (!sid) { setUploadStatus({ ok: false, text: 'No active session — start chat first.' }); return; }
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(
        `/erp-agents/receipt-create/${sid}/upload-receipt-attachment`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const extraction = res.data?.extraction || {};
      if (extraction.success) {
        setUploadStatus({ ok: true, text: `Extracted: ${extraction.receipt_number} (${extraction.confidence} confidence)` });
      } else {
        setUploadStatus({ ok: false, text: extraction.error || 'Could not extract — enter manually.' });
      }
      if (onUploaded) onUploaded({ value: '__ATTACHMENT_UPLOADED__', label: file.name });
    } catch (err) {
      setUploadStatus({ ok: false, text: err?.response?.data?.detail || err.message || 'Upload failed' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mt-2 border border-[#0020C2]/20 rounded-lg overflow-hidden bg-white" data-testid="chat-receipt-attachment">
      <div className="px-3 py-2 text-xs text-gray-600 bg-gray-50 border-b border-gray-100 font-medium">
        Upload receipt document to extract Receipt Number automatically
      </div>
      {uploadStatus && (
        <div className={`px-3 py-1.5 text-xs ${uploadStatus.ok ? 'text-emerald-700 bg-emerald-50' : 'text-red-700 bg-red-50'}`}>
          {uploadStatus.ok ? '[OK] ' : '[ERR] '}{uploadStatus.text}
        </div>
      )}
      <div className="flex">
        <input ref={fileInputRef} type="file"
          accept={fieldUi?.accept || '.pdf,.png,.jpg,.jpeg,.docx,.xlsx'}
          className="hidden" onChange={handleFile} />
        <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
          className="flex-1 text-xs font-bold px-3 py-2 bg-[#0020C2] text-white hover:bg-[#001AA3] disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5">
          {uploading ? 'Uploading...' : (fieldUi?.label || 'Upload Receipt Document')}
        </button>
      </div>
      <div className="px-3 py-1.5 text-[10px] text-gray-400">Supported: PDF, PNG, JPEG, DOCX, XLSX (max 20 MB)</div>
    </div>
  );
}

function MultiSelectChatOptions({ fieldUi, onSubmit }) {
  const [selected, setSelected] = React.useState([]);
  const options = fieldUi.options || [];
  const allowSkip = fieldUi.field === "__classification__";

  const toggle = (value) => {
    setSelected((prev) => prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]);
  };

  return (
    <div className="mt-2 border border-[#0020C2]/20 rounded-lg overflow-hidden bg-white" data-testid={`chat-field-ui-${fieldUi.field}`}>
      <div className="max-h-48 overflow-y-auto divide-y divide-gray-50">
        {options.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">No options available.</div>
        )}
        {options.map((opt) => {
          const checked = selected.includes(opt.value);
          return (
            <label
              key={opt.value}
              className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-[#0020C2]/5 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(opt.value)}
                className="accent-[#0020C2] w-3.5 h-3.5"
              />
              <span className={checked ? "text-[#0020C2] font-medium" : "text-gray-700"}>{opt.label}</span>
            </label>
          );
        })}
      </div>
      <div className="flex">
        <button
          onClick={() => selected.length && onSubmit(selected)}
          disabled={selected.length === 0}
          data-testid="multi-select-submit"
          className="flex-1 text-xs font-bold px-3 py-2 bg-[#0020C2] text-white disabled:bg-gray-200 disabled:text-gray-400 transition-colors"
        >
          {selected.length ? `Select ${selected.length} item${selected.length > 1 ? "s" : ""}` : "Select above"}
        </button>
        {allowSkip && (
          <button
            onClick={() => onSubmit(["none"])}
            data-testid="multi-select-skip"
            className="text-xs font-bold px-3 py-2 bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors border-l border-gray-200"
          >
            None
          </button>
        )}
      </div>
    </div>
  );
}

function SearchableChatOptions({ fieldUi, onPick }) {
  const [q, setQ] = React.useState("");
  const options = fieldUi.options || [];
  const filtered = q ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase())) : options;
  return (
    <div className="mt-2 border border-[#0020C2]/20 rounded-lg overflow-hidden bg-white" data-testid={`chat-field-ui-${fieldUi.field}`}>
      <input
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={`Search ${fieldUi.field.replace(/_/g, " ")}…`}
        className="w-full px-3 py-2 text-xs outline-none border-b border-gray-100"
      />
      <div className="max-h-40 overflow-y-auto">
        {options.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">No options available right now.</div>
        )}
        {options.length > 0 && filtered.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">No matches</div>
        )}
        {filtered.slice(0, 50).map((opt) => (
          <div
            key={opt.value}
            onClick={() => onPick(opt)}
            className="px-3 py-1.5 text-xs hover:bg-[#0020C2]/5 cursor-pointer border-b border-gray-50 last:border-0"
          >
            {opt.label}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Editable GL + Site recommendations widget ── */
function EditableRecommendations({ fieldUi, onSubmit }) {
  const supplier = fieldUi.supplier_name || "";

  const [gl, setGl] = React.useState(fieldUi.gl_account || "");
  const [site, setSite] = React.useState(fieldUi.supplier_site || "");

  const [glOpen, setGlOpen] = React.useState(false);
  const [glQuery, setGlQuery] = React.useState("");
  const [glItems, setGlItems] = React.useState([]);
  const [glLoading, setGlLoading] = React.useState(false);
  const [glManual, setGlManual] = React.useState(false);
  const glDebounce = React.useRef(null);
  const glReq = React.useRef(0);

  const [sites, setSites] = React.useState([]);
  const [siteIdx, setSiteIdx] = React.useState(-1);
  const [sitesLoading, setSitesLoading] = React.useState(false);
  const [sitesFailed, setSitesFailed] = React.useState(false);

  React.useEffect(() => {
    if (!supplier) { setSitesFailed(true); return; }
    let alive = true;
    setSitesLoading(true);
    api.get("/erp-agents/invoice-create/site-lov", { params: { supplier } })
      .then(({ data }) => {
        if (!alive) return;
        const items = data?.items || [];
        setSites(items);
        const rec = fieldUi.supplier_site || "";
        const match = items.findIndex((s) => s.site === rec);
        setSiteIdx(match);
        if (match >= 0) setSite(items[match].site);
        if (items.length === 0) setSitesFailed(true);
      })
      .catch(() => { if (alive) setSitesFailed(true); })
      .finally(() => { if (alive) setSitesLoading(false); });
    return () => { alive = false; };
  }, [supplier]);

  const fetchGl = React.useCallback((q) => {
    if (!supplier) return;
    const myReq = ++glReq.current;
    setGlLoading(true);
    api.get("/erp-agents/invoice-create/gl-lov", { params: { supplier, search: q } })
      .then(({ data }) => {
        if (myReq !== glReq.current) return;
        setGlItems(data?.items || []);
      })
      .catch(() => { if (myReq === glReq.current) setGlItems([]); })
      .finally(() => { if (myReq === glReq.current) setGlLoading(false); });
  }, [supplier]);

  React.useEffect(() => {
    if (!glOpen) return;
    if (glDebounce.current) clearTimeout(glDebounce.current);
    glDebounce.current = setTimeout(() => fetchGl(glQuery.trim()), 300);
    return () => clearTimeout(glDebounce.current);
  }, [glQuery, glOpen, fetchGl]);

  const pickGl = (item) => {
    setGl(item.gl);
    setGlManual(false);
    setGlOpen(false);
    setGlQuery("");
  };

  return (
    <div className="mt-2 border border-[#0020C2]/20 rounded-xl bg-white p-3 space-y-3"
      data-testid="chat-editable-recommendations">
      <div className="text-[10px] font-bold uppercase tracking-wider text-[#0020C2] flex items-center gap-1">
        <Sparkle size={10} weight="fill" /> Recommended — edit if needed
      </div>

      <div className="space-y-1">
        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
          GL Account
        </label>

        <div className="flex items-center gap-1.5">
          <input
            value={gl}
            onChange={(e) => { setGl(e.target.value); setGlManual(true); }}
            placeholder="GL combination"
            className="flex-1 px-3 py-2 text-xs font-mono border border-gray-300 rounded-lg
              focus:outline-none focus:border-[#0020C2]"
          />
          <button
            type="button"
            onClick={() => { setGlOpen((v) => !v); if (!glOpen && glItems.length === 0) fetchGl(""); }}
            className="shrink-0 px-2 py-2 text-[10px] font-bold uppercase tracking-wider
              border border-[#0020C2]/30 text-[#0020C2] rounded-lg hover:bg-[#0020C2]/5
              flex items-center gap-1">
            Browse <CaretDown size={10} weight="bold" className={glOpen ? "rotate-180" : ""} />
          </button>
        </div>

        {glOpen && (
          <div className="border border-[#0020C2]/20 rounded-lg overflow-hidden">
            <input
              autoFocus
              value={glQuery}
              onChange={(e) => setGlQuery(e.target.value)}
              placeholder='Search past GL by description — e.g. "electronic"'
              className="w-full px-3 py-2 text-xs outline-none border-b border-gray-100"
            />
            <div className="max-h-44 overflow-y-auto">
              {glLoading && (
                <div className="px-3 py-2 text-[11px] text-gray-400 flex items-center gap-2">
                  <span className="w-3 h-3 border-2 border-gray-300 border-t-[#0020C2] rounded-full animate-spin" />
                  Loading…
                </div>
              )}
              {!glLoading && glItems.length === 0 && (
                <div className="px-3 py-2 text-[11px] text-gray-400">
                  {glQuery.trim()
                    ? `No past GL matches "${glQuery.trim()}". Keeping the recommended GL — you can also type one above.`
                    : "No historical GL combinations for this supplier. Keep the recommended GL, or type one above."}
                </div>
              )}
              {!glLoading && glItems.map((it, i) => (
                <button
                  type="button"
                  key={`${it.gl}-${i}`}
                  onClick={() => pickGl(it)}
                  className="w-full text-left px-3 py-1.5 hover:bg-[#0020C2]/5 border-b border-gray-50 last:border-0">
                  <div className="font-mono text-[11px] text-gray-900">{it.gl}</div>
                  <div className="text-[10px] text-gray-500 flex items-center justify-between gap-2">
                    <span className="truncate">{it.description || "—"}</span>
                    <span className="shrink-0 text-gray-400">
                      {it.use_count > 1 ? `used ${it.use_count}×` : ""}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {fieldUi.gl_reason && !glOpen && (
          <p className="text-[10px] text-gray-500">
            {glManual ? "Custom GL entered." : fieldUi.gl_reason}
          </p>
        )}
      </div>

      <div className="space-y-1">
        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
          Supplier Site
        </label>

        {sitesLoading ? (
          <div className="px-3 py-2 text-[11px] text-gray-400 flex items-center gap-2 border border-gray-200 rounded-lg">
            <span className="w-3 h-3 border-2 border-gray-300 border-t-[#0020C2] rounded-full animate-spin" />
            Loading sites…
          </div>
        ) : sitesFailed || sites.length === 0 ? (
          <input
            value={site}
            onChange={(e) => setSite(e.target.value)}
            className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg
              focus:outline-none focus:border-[#0020C2]"
          />
        ) : (
          <select
            value={siteIdx}
            onChange={(e) => {
              const idx = parseInt(e.target.value, 10);
              setSiteIdx(idx);
              setSite(idx < 0 ? (fieldUi.supplier_site || "") : (sites[idx]?.site || ""));
            }}
            className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg bg-white
              focus:outline-none focus:border-[#0020C2]">
            {fieldUi.supplier_site && !sites.some((s) => s.site === fieldUi.supplier_site) && (
              <option value={-1}>{fieldUi.supplier_site} (recommended)</option>
            )}
            {sites.map((s, i) => (
              <option key={`${s.site}-${s.procurement_bu}-${i}`} value={i}>
                {s.site}
                {s.procurement_bu ? ` · ${s.procurement_bu}` : ""}
                {s.pay_site ? " · pay" : ""}
              </option>
            ))}
          </select>
        )}

        {fieldUi.site_reason && (
          <p className="text-[10px] text-gray-500">{fieldUi.site_reason}</p>
        )}
      </div>

      <button
        onClick={() => onSubmit(`RECOMMENDATIONS: gl=${gl.trim()}; site=${site.trim()}`)}
        className="w-full bg-[#0020C2] text-white py-2 rounded-lg text-xs font-bold
          uppercase tracking-wider hover:bg-[#001AA3]">
        Confirm GL &amp; Site
      </button>
    </div>
  );
}

/* ── Line-items builder widget (per-line tax, codes from config) ── */
function LineItemsBuilder({ fieldUi, onSubmit }) {
  const suggestedCurrencies =
    (fieldUi.currency_choices && fieldUi.currency_choices.length
      ? fieldUi.currency_choices
      : [{ code: fieldUi.currency || "AED", name: "" }]);
  const [currency, setCurrency] = React.useState(fieldUi.currency || suggestedCurrencies[0].code);
  const [ccyOpen, setCcyOpen] = React.useState(false);
  const [ccyQuery, setCcyQuery] = React.useState("");
  const [ccyResults, setCcyResults] = React.useState([]);
  const [ccyLoading, setCcyLoading] = React.useState(false);
  const [ccyHasMore, setCcyHasMore] = React.useState(false);
  const [ccyOffset, setCcyOffset] = React.useState(0);
  const ccyDebounce = React.useRef(null);
  const ccyReq = React.useRef(0);
  const ccyListRef = React.useRef(null);
  const CCY_PAGE = 25;

  const fetchCurrencies = React.useCallback((q, offset, append) => {
    const myReq = ++ccyReq.current;
    setCcyLoading(true);
    api.get("/erp-agents/invoice-create/currency-lov", {
      params: {
        business_unit: fieldUi.business_unit || "",
        profile_default: fieldUi.profile_default_currency || "",
        search: q,
        limit: CCY_PAGE,
        offset,
      },
    })
      .then(({ data }) => {
        if (myReq !== ccyReq.current) return;
        const items = data?.items || [];
        setCcyResults((prev) => (append ? [...prev, ...items] : items));
        setCcyHasMore(!!data?.has_more);
        setCcyOffset(offset + items.length);
      })
      .catch(() => {
        if (myReq === ccyReq.current && !append) setCcyResults([]);
      })
      .finally(() => { if (myReq === ccyReq.current) setCcyLoading(false); });
  }, [fieldUi.business_unit, fieldUi.profile_default_currency]);

  React.useEffect(() => {
    if (!ccyOpen) return;
    if (ccyDebounce.current) clearTimeout(ccyDebounce.current);
    ccyDebounce.current = setTimeout(() => {
      setCcyOffset(0);
      fetchCurrencies(ccyQuery.trim(), 0, false);
      if (ccyListRef.current) ccyListRef.current.scrollTop = 0;
    }, 300);
    return () => clearTimeout(ccyDebounce.current);
  }, [ccyQuery, ccyOpen, fetchCurrencies]);

  const onCcyScroll = (e) => {
    const el = e.target;
    if (!ccyLoading && ccyHasMore && el.scrollHeight - el.scrollTop - el.clientHeight < 40) {
      fetchCurrencies(ccyQuery.trim(), ccyOffset, true);
    }
  };

  const pickCurrency = (code) => {
    setCurrency(code);
    setCcyOpen(false);
    setCcyQuery("");
    setCcyResults([]);
    setCcyOffset(0);
  };
  const taxCodes = fieldUi.tax_codes || [];
  const hasCodes = taxCodes.length > 0;

  const defaultCode = React.useMemo(
    () =>
      taxCodes.find((t) => t.code === fieldUi.tax_default_code) ||
      taxCodes.find((t) => t.is_default) ||
      taxCodes[0] ||
      null,
    [taxCodes, fieldUi.tax_default_code]
  );

  const [rows, setRows] = React.useState(() => {
    const pf = fieldUi.prefill_lines;
    if (Array.isArray(pf) && pf.length) {
      return pf.map((l) => ({
        description: l.description || "",
        quantity: l.quantity != null ? String(l.quantity) : "",
        unit_price: l.unit_price != null ? String(l.unit_price) : "",
        tax_code: l.tax_code || defaultCode?.code || "",
      }));
    }
    return [{ description: "", quantity: "", unit_price: "", tax_code: defaultCode?.code || "" }];
  });

  const codeOf = (code) => taxCodes.find((t) => t.code === code) || null;

  const updateRow = (i, key, val) =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  const addRow = () =>
    setRows((prev) => [
      ...prev,
      { description: "", quantity: "", unit_price: "", tax_code: defaultCode?.code || "" },
    ]);
  const removeRow = (i) =>
    setRows((prev) => (prev.length === 1 ? prev : prev.filter((_, idx) => idx !== i)));

  const calc = rows.reduce(
    (acc, r) => {
      const q = parseFloat(r.quantity) || 0;
      const p = parseFloat(r.unit_price) || 0;
      const net = q * p;
      const t = codeOf(r.tax_code);
      const rate = t ? t.rate : 0;
      acc.net += net;
      if (t && t.grosses_up) acc.payableTax += net * (rate / 100);
      else if (t && !t.grosses_up && rate > 0) acc.selfTax += net * (rate / 100);
      return acc;
    },
    { net: 0, payableTax: 0, selfTax: 0 }
  );
  const total = calc.net + calc.payableTax;

  const rowFilled = (r) =>
    r.description.trim() && parseFloat(r.quantity) > 0 && parseFloat(r.unit_price) > 0;
  const rowEmpty = (r) => !r.description.trim() && !r.quantity && !r.unit_price;

  const canSubmit =
    rows.every((r) => rowEmpty(r) || (rowFilled(r) && (!hasCodes || r.tax_code))) &&
    rows.some(rowFilled);

  const submit = () => {
    const clean = rows.filter(rowFilled).map((r) => {
      const t = codeOf(r.tax_code);
      const line = {
        description: r.description.trim(),
        quantity: parseFloat(r.quantity),
        unit_price: parseFloat(r.unit_price),
      };
      if (r.tax_code) {
        line.tax_code = r.tax_code;
        line.tax_rate = t ? t.rate : 0;
        line.tax_grosses_up = t ? !!t.grosses_up : false;
      }
      return line;
    });
    onSubmit(`LINES: ${JSON.stringify({ lines: clean, currency })}`);
  };

  const label = (t) =>
    `${t.code} (${t.rate}%)${!t.grosses_up && t.rate > 0 ? " · self-assessed" : ""}`;

  return (
    <div className="mt-2 border border-[#0020C2]/20 rounded-xl bg-white p-3 space-y-2"
      data-testid="chat-line-items-builder">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] font-bold uppercase tracking-wider text-[#0020C2] flex items-center gap-1">
          <Sparkle size={10} weight="fill" /> Invoice line items
        </div>
        <button
          type="button"
          onClick={() => { const nx=!ccyOpen; setCcyOpen(nx); if (nx) { setCcyOffset(0); fetchCurrencies("", 0, false); } }}
          title="Invoice currency"
          className="text-[11px] font-mono border border-gray-300 rounded px-2 py-1 bg-white
            hover:border-[#0020C2] flex items-center gap-1">
          {currency} <CaretDown size={9} weight="bold" className={ccyOpen ? "rotate-180" : ""} />
        </button>
      </div>

      {ccyOpen && (
        <div className="border border-[#0020C2]/20 rounded-lg overflow-hidden">
          <input
            autoFocus
            value={ccyQuery}
            onChange={(e) => setCcyQuery(e.target.value)}
            placeholder='Search currency — e.g. "yen", "JPY", "euro"'
            className="w-full px-3 py-2 text-xs outline-none border-b border-gray-100"
          />
          <div ref={ccyListRef} onScroll={onCcyScroll} className="max-h-44 overflow-y-auto">
            {ccyResults.map((c) => (
              <button
                type="button"
                key={c.code}
                onClick={() => pickCurrency(c.code)}
                className="w-full text-left px-3 py-1.5 hover:bg-[#0020C2]/5 border-b border-gray-50 last:border-0
                  flex items-center gap-2">
                <span className="font-mono text-[11px] text-gray-900 w-10">{c.code}</span>
                <span className="text-[10px] text-gray-500 truncate">{c.name || ""}</span>
              </button>
            ))}
            {ccyLoading && (
              <div className="px-3 py-2 text-[11px] text-gray-400 flex items-center gap-2">
                <span className="w-3 h-3 border-2 border-gray-300 border-t-[#0020C2] rounded-full animate-spin" />
                Loading…
              </div>
            )}
            {!ccyLoading && ccyQuery.trim() && ccyResults.length === 0 && (
              <div className="px-3 py-2 text-[11px] text-gray-400">No currency matches "{ccyQuery.trim()}".</div>
            )}
            {!ccyLoading && ccyHasMore && (
              <div className="px-3 py-1.5 text-[10px] text-gray-400 text-center">Scroll for more…</div>
            )}
          </div>
        </div>
      )}

      {rows.map((r, i) => (
        <div key={i} className="space-y-1">
          <div className="flex gap-1.5 items-center">
            <input
              value={r.description}
              onChange={(e) => updateRow(i, "description", e.target.value)}
              placeholder="Product / description"
              className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded
                focus:outline-none focus:border-[#0020C2]"
            />
            <input
              value={r.quantity}
              onChange={(e) => updateRow(i, "quantity", e.target.value)}
              placeholder="Qty"
              inputMode="decimal"
              className="w-14 px-2 py-1.5 text-xs border border-gray-300 rounded text-right
                focus:outline-none focus:border-[#0020C2]"
            />
            <input
              value={r.unit_price}
              onChange={(e) => updateRow(i, "unit_price", e.target.value)}
              placeholder="Price"
              inputMode="decimal"
              className="w-20 px-2 py-1.5 text-xs border border-gray-300 rounded text-right
                focus:outline-none focus:border-[#0020C2]"
            />
            <button
              onClick={() => removeRow(i)}
              className="text-[#DC2626] shrink-0 p-1 disabled:opacity-30"
              disabled={rows.length === 1}
              title="Remove line">
              <Trash size={12} weight="bold" />
            </button>
          </div>

          <div className="flex items-center gap-1.5 pl-0.5">
            <span className="text-[10px] uppercase tracking-wider text-gray-400 w-8 shrink-0">Tax</span>
            {hasCodes ? (
              <select
                value={r.tax_code}
                onChange={(e) => updateRow(i, "tax_code", e.target.value)}
                className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded bg-white
                  focus:outline-none focus:border-[#0020C2]">
                {taxCodes.map((t) => (
                  <option key={t.code} value={t.code}>{label(t)}</option>
                ))}
              </select>
            ) : (
              <input
                value={r.tax_code}
                onChange={(e) => updateRow(i, "tax_code", e.target.value)}
                placeholder="Tax classification (optional)"
                className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded
                  focus:outline-none focus:border-[#0020C2]"
              />
            )}
          </div>
        </div>
      ))}

      <button
        onClick={addRow}
        className="text-[11px] font-bold text-[#0020C2] hover:underline flex items-center gap-1">
        + Add line
      </button>

      <div className="bg-gray-50 rounded-lg p-2 text-xs space-y-0.5">
        <div className="flex justify-between text-gray-600">
          <span>Subtotal</span>
          <span className="font-mono">{currency} {calc.net.toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-gray-600">
          <span>Tax</span>
          <span className="font-mono">{currency} {calc.payableTax.toFixed(2)}</span>
        </div>
        {calc.selfTax > 0 && (
          <div className="flex justify-between text-gray-400">
            <span>Self-assessed (RCM, not payable)</span>
            <span className="font-mono">{currency} {calc.selfTax.toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between font-bold text-gray-900 pt-0.5 border-t border-gray-200">
          <span>Total</span>
          <span className="font-mono">{currency} {total.toFixed(2)}</span>
        </div>
      </div>

      <button
        onClick={submit}
        disabled={!canSubmit}
        className="w-full bg-[#0020C2] text-white py-2 rounded-lg text-xs font-bold
          uppercase tracking-wider hover:bg-[#001AA3] disabled:opacity-40">
        Submit line items
      </button>
    </div>
  );
}

/* ── End-of-flow multi-PDF attachment upload (skippable) ── */
function InvoiceAttachmentUpload({ onUpload, onSkip, busy }) {
  const fileInputRef = React.useRef(null);
  const [files, setFiles] = React.useState([]);

  const addFiles = (picked) => setFiles((prev) => [...prev, ...picked]);
  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  return (
    <div className="mt-2 border border-[#0020C2]/20 rounded-xl bg-white p-3 space-y-3"
      data-testid="chat-invoice-attachment-upload">
      <div className="text-[10px] font-bold uppercase tracking-wider text-[#0020C2] flex items-center gap-1">
        <Sparkle size={10} weight="fill" /> Add supporting documents (optional)
      </div>
      <p className="text-[11px] text-gray-500">
        Attach one or more PDFs to this invoice, or skip. Files are attached as-is.
      </p>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg"
        multiple
        className="hidden"
        onChange={(e) => {
          const picked = Array.from(e.target.files || []);
          e.target.value = "";
          if (picked.length) addFiles(picked);
        }}
      />

      {files.length > 0 && (
        <div className="space-y-1">
          {files.map((f, i) => (
            <div key={i} className="flex items-center gap-1.5 text-[11px] text-gray-700">
              <Paperclip size={11} weight="bold" className="text-gray-400 shrink-0" />
              <span className="truncate flex-1">{f.name}</span>
              <button onClick={() => removeFile(i)} className="text-[#DC2626] shrink-0 p-1" title="Remove">
                <Trash size={11} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={() => fileInputRef.current?.click()}
        className="text-[11px] font-bold text-[#0020C2] hover:underline flex items-center gap-1">
        + Add PDF
      </button>

      <div className="flex gap-2">
        <button
          onClick={() => files.length && onUpload(files)}
          disabled={!files.length || busy}
          className="flex-1 bg-[#0020C2] text-white py-2 rounded-lg text-xs font-bold
            uppercase tracking-wider hover:bg-[#001AA3] disabled:opacity-40">
          {busy ? "Uploading…" : `Send ${files.length || ""} document${files.length === 1 ? "" : "s"}`.trim()}
        </button>
        <button
          onClick={onSkip}
          disabled={busy}
          className="flex-1 border border-gray-300 text-gray-600 py-2 rounded-lg
            text-xs font-bold uppercase tracking-wider hover:bg-gray-50 disabled:opacity-40">
          Skip
        </button>
      </div>
    </div>
  );
}

/* ── Markdown renderer for assistant messages ── */
function ChatMarkdown({ children }) {
  return (
    <div className="chat-md text-sm leading-relaxed">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ node, ...props }) => <p className="mb-2 last:mb-0 whitespace-pre-wrap" {...props} />,
          strong: ({ node, ...props }) => <strong className="font-bold text-gray-900" {...props} />,
          ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-2 space-y-0.5" {...props} />,
          ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-2 space-y-0.5" {...props} />,
          li: ({ node, ...props }) => <li className="text-sm" {...props} />,
          h1: ({ node, ...props }) => <h1 className="text-base font-bold mb-1 mt-2" {...props} />,
          h2: ({ node, ...props }) => <h2 className="text-sm font-bold mb-1 mt-2" {...props} />,
          h3: ({ node, ...props }) => <h3 className="text-sm font-bold mb-1 mt-2 text-[#0020C2]" {...props} />,
          code: ({ node, inline, ...props }) =>
            inline
              ? <code className="font-mono text-[12px] bg-gray-100 px-1 py-0.5 rounded" {...props} />
              : <code className="font-mono text-[12px] block bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto" {...props} />,
          table: ({ node, ...props }) => (
            <div className="overflow-x-auto my-2">
              <table className="w-full text-xs border-collapse" {...props} />
            </div>
          ),
          thead: ({ node, ...props }) => <thead className="bg-[#0020C2]/5" {...props} />,
          th: ({ node, ...props }) => (
            <th className="border border-gray-200 px-2 py-1.5 text-left font-bold uppercase tracking-wider text-[10px] text-gray-600" {...props} />
          ),
          td: ({ node, ...props }) => (
            <td className="border border-gray-200 px-2 py-1.5 text-gray-800" {...props} />
          ),
          a: ({ node, ...props }) => <a className="text-[#0020C2] underline" {...props} />,
          hr: () => <hr className="my-2 border-gray-200" />,
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}

/* ── Supplier Ageing dashboard widget ── */
function AgeingDashboard({ widget }) {
  if (!widget || !widget.currencies) return null;

  const BUCKET_COLORS = {
    "Current": "#10B981",
    "1-30":    "#3B82F6",
    "31-60":   "#F59E0B",
    "61-90":   "#F97316",
    "90+":     "#DC2626",
  };
  const RISK_COLORS = {
    LOW: "#10B981", MEDIUM: "#F59E0B", HIGH: "#F97316", CRITICAL: "#DC2626",
  };

  const currencies = Object.entries(widget.currencies);

  return (
    <div className="mt-2 space-y-3" data-testid="ageing-dashboard">
      <div className="text-[10px] font-bold uppercase tracking-wider text-[#0020C2] flex items-center gap-1">
        <Sparkle size={10} weight="fill" />
        Ageing — {widget.scope_type === "supplier" ? "Supplier" : "Business Unit"}: {widget.scope_value}
        {widget.approximate && (
          <span className="ml-1 text-[9px] font-normal text-amber-600 normal-case">
            (BU estimate)
          </span>
        )}
      </div>

      {currencies.map(([currency, c]) => {
        const total = c.total_liability || 0;
        return (
          <div key={currency} className="border border-gray-200 rounded-xl bg-white p-3 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm font-black text-gray-900">{currency}</div>
              <div className="flex items-center gap-3 text-[11px]">
                <div className="text-right">
                  <div className="text-gray-400 uppercase tracking-wider text-[9px] font-bold">Owed</div>
                  <div className="font-bold text-gray-900">{currency} {total.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                </div>
                <div className="text-right">
                  <div className="text-gray-400 uppercase tracking-wider text-[9px] font-bold">Overdue</div>
                  <div className="font-bold text-gray-900">{c.overdue_ratio}%</div>
                </div>
                <div className="text-right">
                  <div className="text-gray-400 uppercase tracking-wider text-[9px] font-bold">Risk</div>
                  <div className="font-bold" style={{ color: RISK_COLORS[c.risk_level] || "#374151" }}>
                    {c.risk_score}/100 · {c.risk_level}
                  </div>
                </div>
              </div>
            </div>

            {total > 0 && (
              <div className="space-y-1">
                <div className="flex h-3 rounded-full overflow-hidden bg-gray-100">
                  {c.buckets.map((b) => {
                    const pct = total > 0 ? (b.amount / total) * 100 : 0;
                    if (pct <= 0) return null;
                    return (
                      <div
                        key={b.bucket}
                        style={{ width: `${pct}%`, background: BUCKET_COLORS[b.bucket] || "#9CA3AF" }}
                        title={`${b.bucket}: ${currency} ${b.amount.toLocaleString()} (${b.percentage}%)`}
                      />
                    );
                  })}
                </div>
                <div className="flex flex-wrap gap-2">
                  {c.buckets.map((b) => (
                    <span key={b.bucket} className="inline-flex items-center gap-1 text-[9px] text-gray-600">
                      <span className="w-2 h-2 rounded-sm" style={{ background: BUCKET_COLORS[b.bucket] || "#9CA3AF" }} />
                      {b.bucket}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead className="bg-[#0020C2]/5">
                  <tr>
                    <th className="border border-gray-200 px-2 py-1 text-left text-[10px] font-bold uppercase tracking-wider text-gray-600">Bucket</th>
                    <th className="border border-gray-200 px-2 py-1 text-right text-[10px] font-bold uppercase tracking-wider text-gray-600">Amount</th>
                    <th className="border border-gray-200 px-2 py-1 text-right text-[10px] font-bold uppercase tracking-wider text-gray-600">Inv</th>
                    <th className="border border-gray-200 px-2 py-1 text-right text-[10px] font-bold uppercase tracking-wider text-gray-600">%</th>
                  </tr>
                </thead>
                <tbody>
                  {c.buckets.map((b) => (
                    <tr key={b.bucket}>
                      <td className="border border-gray-200 px-2 py-1">
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-sm" style={{ background: BUCKET_COLORS[b.bucket] || "#9CA3AF" }} />
                          {b.bucket}
                        </span>
                      </td>
                      <td className="border border-gray-200 px-2 py-1 text-right font-mono">{currency} {b.amount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                      <td className="border border-gray-200 px-2 py-1 text-right">{b.invoice_count}</td>
                      <td className="border border-gray-200 px-2 py-1 text-right">{b.percentage}%</td>
                    </tr>
                  ))}
                  <tr className="font-bold bg-gray-50">
                    <td className="border border-gray-200 px-2 py-1">Total</td>
                    <td className="border border-gray-200 px-2 py-1 text-right font-mono">{currency} {total.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                    <td className="border border-gray-200 px-2 py-1 text-right">{c.invoice_count}</td>
                    <td className="border border-gray-200 px-2 py-1 text-right">100%</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {c.top_suppliers && c.top_suppliers.length > 0 && widget.scope_type === "business_unit" && (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Top suppliers</div>
                <div className="space-y-1">
                  {c.top_suppliers.map((s) => (
                    <div key={s.supplier_name} className="flex items-center gap-2 text-[11px]">
                      <div className="min-w-0 flex-1 truncate text-gray-700">{s.supplier_name}</div>
                      <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-[#0020C2]" style={{ width: `${s.percentage_of_total}%` }} />
                      </div>
                      <div className="font-mono text-gray-900 w-28 text-right">{currency} {s.total_outstanding.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {c.insights && c.insights.length > 0 && (
              <div className="space-y-1">
                {c.insights.map((ins, i) => {
                  const color = ins.severity === "critical" ? "text-red-700 bg-red-50 border-red-200"
                    : ins.severity === "warning" ? "text-amber-700 bg-amber-50 border-amber-200"
                    : "text-blue-700 bg-blue-50 border-blue-200";
                  return (
                    <div key={i} className={`text-[11px] px-2 py-1 rounded border ${color}`}>
                      {ins.message}
                    </div>
                  );
                })}
              </div>
            )}

            {c.recommendations && c.recommendations.length > 0 && (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Recommended actions</div>
                <ul className="list-disc pl-4 space-y-0.5 text-[11px] text-gray-700">
                  {c.recommendations.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}
          </div>
        );
      })}

      {widget.notes && (widget.notes.on_hold_count > 0 || widget.notes.excluded_unvalidated_count > 0) && (
        <div className="text-[10px] text-gray-400 space-y-0.5">
          {widget.notes.on_hold_count > 0 && <div>· {widget.notes.on_hold_count} invoice(s) on hold (included, flagged)</div>}
          {widget.notes.excluded_unvalidated_count > 0 && <div>· {widget.notes.excluded_unvalidated_count} unvalidated invoice(s) excluded from totals</div>}
        </div>
      )}
    </div>
  );
}

/* ── Supplier / Business Unit LOV picker (supplier-ageing) ── */
function SupplierAgeingLov({ kind = "supplier", onPick }) {
  const [q, setQ] = React.useState("");
  const [items, setItems] = React.useState([]);
  const [offset, setOffset] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState("");
  const listRef = React.useRef(null);
  const debounceRef = React.useRef(null);
  const reqIdRef = React.useRef(0);

  const PAGE = 25;
  const path = kind === "business_unit"
    ? "/erp-agents/supplier-ageing/lov/business-units"
    : "/erp-agents/supplier-ageing/lov/suppliers";
  const noun = kind === "business_unit" ? "business unit" : "supplier";

  const fetchPage = React.useCallback(async (search, nextOffset, append) => {
    const myReq = ++reqIdRef.current;
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get(path, {
        params: { search, offset: nextOffset, limit: PAGE },
      });
      if (myReq !== reqIdRef.current) return;
      const newItems = data?.items || [];
      setItems((prev) => (append ? [...prev, ...newItems] : newItems));
      setHasMore(!!data?.has_more);
      setOffset(nextOffset + newItems.length);
      if (data?.error) setErr("Couldn't load — check the Oracle profile.");
    } catch (e) {
      if (myReq !== reqIdRef.current) return;
      setErr(e?.response?.data?.detail || e.message || "Load failed");
    } finally {
      if (myReq === reqIdRef.current) setLoading(false);
    }
  }, [path]);

  React.useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setOffset(0);
      fetchPage(q.trim(), 0, false);
      if (listRef.current) listRef.current.scrollTop = 0;
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [q, fetchPage]);

  const onScroll = (e) => {
    const el = e.target;
    if (!loading && hasMore && el.scrollHeight - el.scrollTop - el.clientHeight < 40) {
      fetchPage(q.trim(), offset, true);
    }
  };

  return (
    <div className="mt-2 border border-[#0020C2]/25 rounded-xl bg-white overflow-hidden shadow-sm"
      data-testid={`ageing-lov-${kind}`}>
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-[#0020C2]/5">
        <Sparkle size={11} weight="fill" className="text-[#0020C2]" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#0020C2]">
          Browse {noun}s
        </span>
      </div>

      <div className="p-2">
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`Type to search ${noun}s — e.g. "garden"`}
          className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg
            focus:outline-none focus:border-[#0020C2]"
        />
      </div>

      <div ref={listRef} onScroll={onScroll} className="max-h-64 overflow-y-auto">
        {err && (
          <div className="px-3 py-2 text-xs text-red-600">{err}</div>
        )}
        {!err && items.length === 0 && !loading && (
          <div className="px-3 py-3 text-xs text-gray-400">
            No {noun}s found{q.trim() ? ` for "${q.trim()}"` : ""}.
          </div>
        )}
        {items.map((it, i) => (
          <button
            key={`${it.name}-${i}`}
            onClick={() => onPick?.(it.name)}
            className="w-full text-left px-3 py-2 text-xs hover:bg-[#0020C2]/5
              border-b border-gray-50 last:border-0 flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate text-gray-800">{it.name}</span>
            {it.number && (
              <span className="text-[10px] text-gray-400 font-mono shrink-0">#{it.number}</span>
            )}
          </button>
        ))}
        {loading && (
          <div className="px-3 py-2 text-xs text-gray-400 flex items-center gap-2">
            <span className="w-3 h-3 border-2 border-gray-300 border-t-[#0020C2] rounded-full animate-spin" />
            Loading…
          </div>
        )}
        {!loading && hasMore && (
          <div className="px-3 py-1.5 text-[10px] text-gray-400 text-center">Scroll for more…</div>
        )}
      </div>
    </div>
  );
}

/* ── Invoice-create Business Unit browser ── */
function InvoiceBusinessUnitBrowser({ onPick }) {
  const [q, setQ] = React.useState("");
  const [items, setItems] = React.useState([]);
  const [offset, setOffset] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState("");
  const listRef = React.useRef(null);
  const debounceRef = React.useRef(null);
  const reqIdRef = React.useRef(0);
  const PAGE = 25;

  const fetchPage = React.useCallback(async (search, nextOffset, append) => {
    const myReq = ++reqIdRef.current;
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/erp-agents/invoice-create/business-unit-lov", {
        params: { search, offset: nextOffset, limit: PAGE },
      });
      if (myReq !== reqIdRef.current) return;
      const newItems = data?.items || [];
      setItems((prev) => (append ? [...prev, ...newItems] : newItems));
      setHasMore(!!data?.has_more);
      setOffset(nextOffset + newItems.length);
      if (data?.error) setErr("Couldn't load — check the Oracle profile.");
    } catch (e) {
      if (myReq !== reqIdRef.current) return;
      setErr(e?.response?.data?.detail || e.message || "Load failed");
    } finally {
      if (myReq === reqIdRef.current) setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setOffset(0);
      fetchPage(q.trim(), 0, false);
      if (listRef.current) listRef.current.scrollTop = 0;
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [q, fetchPage]);

  const onScroll = (e) => {
    const el = e.target;
    if (!loading && hasMore && el.scrollHeight - el.scrollTop - el.clientHeight < 40) {
      fetchPage(q.trim(), offset, true);
    }
  };

  return (
    <div className="mt-2 border border-[#0020C2]/25 rounded-xl bg-white overflow-hidden shadow-sm"
      data-testid="chat-bu-browser">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-[#0020C2]/5">
        <Sparkle size={11} weight="fill" className="text-[#0020C2]" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#0020C2]">
          Browse business units
        </span>
      </div>
      <div className="p-2">
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Type to search business units…"
          className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg
            focus:outline-none focus:border-[#0020C2]"
        />
      </div>
      <div ref={listRef} onScroll={onScroll} className="max-h-64 overflow-y-auto">
        {err && <div className="px-3 py-2 text-xs text-red-600">{err}</div>}
        {!err && items.length === 0 && !loading && (
          <div className="px-3 py-3 text-xs text-gray-400">
            No business units found{q.trim() ? ` for "${q.trim()}"` : ""}.
          </div>
        )}
        {items.map((it, i) => (
          <button
            key={`${it.name}-${i}`}
            onClick={() => onPick?.(it.name)}
            className="w-full text-left px-3 py-2 text-xs hover:bg-[#0020C2]/5
              border-b border-gray-50 last:border-0 flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate text-gray-800">{it.name}</span>
            {it.number && (
              <span className="text-[10px] text-gray-400 font-mono shrink-0">#{it.number}</span>
            )}
          </button>
        ))}
        {loading && (
          <div className="px-3 py-2 text-xs text-gray-400 flex items-center gap-2">
            <span className="w-3 h-3 border-2 border-gray-300 border-t-[#0020C2] rounded-full animate-spin" />
            Loading…
          </div>
        )}
        {!loading && hasMore && (
          <div className="px-3 py-1.5 text-[10px] text-gray-400 text-center">Scroll for more…</div>
        )}
      </div>
    </div>
  );
}

/* ── Invoice-create Supplier browser ── */
function InvoiceSupplierBrowser({ onPick }) {
  const [q, setQ] = React.useState("");
  const [items, setItems] = React.useState([]);
  const [offset, setOffset] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState("");
  const listRef = React.useRef(null);
  const debounceRef = React.useRef(null);
  const reqIdRef = React.useRef(0);
  const PAGE = 25;

  const fetchPage = React.useCallback(async (search, nextOffset, append) => {
    const myReq = ++reqIdRef.current;
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/erp-agents/invoice-create/supplier-lov", {
        params: { search, offset: nextOffset, limit: PAGE },
      });
      if (myReq !== reqIdRef.current) return;
      const newItems = data?.items || [];
      setItems((prev) => (append ? [...prev, ...newItems] : newItems));
      setHasMore(!!data?.has_more);
      setOffset(nextOffset + newItems.length);
      if (data?.error) setErr("Couldn't load — check the Oracle profile.");
    } catch (e) {
      if (myReq !== reqIdRef.current) return;
      setErr(e?.response?.data?.detail || e.message || "Load failed");
    } finally {
      if (myReq === reqIdRef.current) setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setOffset(0);
      fetchPage(q.trim(), 0, false);
      if (listRef.current) listRef.current.scrollTop = 0;
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [q, fetchPage]);

  const onScroll = (e) => {
    const el = e.target;
    if (!loading && hasMore && el.scrollHeight - el.scrollTop - el.clientHeight < 40) {
      fetchPage(q.trim(), offset, true);
    }
  };

  return (
    <div className="mt-2 border border-[#0020C2]/25 rounded-xl bg-white overflow-hidden shadow-sm"
      data-testid="chat-supplier-browser">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-[#0020C2]/5">
        <Sparkle size={11} weight="fill" className="text-[#0020C2]" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#0020C2]">
          Browse suppliers
        </span>
      </div>
      <div className="p-2">
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Type to search suppliers…"
          className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg
            focus:outline-none focus:border-[#0020C2]"
        />
      </div>
      <div ref={listRef} onScroll={onScroll} className="max-h-64 overflow-y-auto">
        {err && <div className="px-3 py-2 text-xs text-red-600">{err}</div>}
        {!err && items.length === 0 && !loading && (
          <div className="px-3 py-3 text-xs text-gray-400">
            No suppliers found{q.trim() ? ` for "${q.trim()}"` : ""}.
          </div>
        )}
        {items.map((it, i) => (
          <button
            key={`${it.name}-${i}`}
            onClick={() => onPick?.(it.name)}
            className="w-full text-left px-3 py-2 text-xs hover:bg-[#0020C2]/5
              border-b border-gray-50 last:border-0 flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate text-gray-800">{it.name}</span>
            {it.number && (
              <span className="text-[10px] text-gray-400 font-mono shrink-0">#{it.number}</span>
            )}
          </button>
        ))}
        {loading && (
          <div className="px-3 py-2 text-xs text-gray-400 flex items-center gap-2">
            <span className="w-3 h-3 border-2 border-gray-300 border-t-[#0020C2] rounded-full animate-spin" />
            Loading…
          </div>
        )}
        {!loading && hasMore && (
          <div className="px-3 py-1.5 text-[10px] text-gray-400 text-center">Scroll for more…</div>
        )}
      </div>
    </div>
  );
}

/* ── PO line review widget (per-line tax, like the PDF review) ────────────── */
function PoLineReview({ fieldUi, onSubmit }) {
  const currency = fieldUi.currency || "AED";
  const taxCodes = fieldUi.tax_codes || [];
  const defaultCode =
    taxCodes.find((t) => t.code === fieldUi.tax_default_code) ||
    taxCodes.find((t) => t.is_default) || taxCodes[0] || null;

  const [rows, setRows] = React.useState(
    (fieldUi.lines || []).map((l) => ({
      line_number: l.line_number,
      item_number: l.item_number || "",
      description: l.description || "",
      quantity: l.quantity,
      unit_price: l.unit_price,
      amount: l.amount,
      tax_code: l.tax_code || (defaultCode ? defaultCode.code : ""),
    }))
  );

  const setTax = (i, code) => setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, tax_code: code } : r)));

  const total = rows.reduce((s, r) => s + (Number(r.amount) || (Number(r.quantity) || 0) * (Number(r.unit_price) || 0)), 0);

  const submit = () => {
    onSubmit(`PO_LINES: ${JSON.stringify({
      lines: rows.map((r) => ({ line_number: r.line_number, tax_code: r.tax_code || null })),
    })}`);
  };

  return (
    <div className="mt-2 border border-[#0020C2]/20 rounded-xl bg-white p-3 space-y-2" data-testid="chat-po-line-review">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-wider text-[#0020C2] flex items-center gap-1">
          <Sparkle size={10} weight="fill" /> PO {fieldUi.po_number} · {fieldUi.match_type || "match"}
        </div>
        <span className="text-[11px] font-mono text-gray-500">{currency} {total.toFixed(2)}</span>
      </div>

      <div className="border border-gray-200 rounded-lg overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-gray-500">
            <tr className="text-left">
              <th className="px-2 py-1.5 font-bold">#</th>
              <th className="px-2 py-1.5 font-bold">Description</th>
              <th className="px-2 py-1.5 font-bold text-right">Qty</th>
              <th className="px-2 py-1.5 font-bold text-right">Price</th>
              <th className="px-2 py-1.5 font-bold text-right">Amount</th>
              <th className="px-2 py-1.5 font-bold">Tax</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-gray-100">
                <td className="px-2 py-1">{r.line_number}</td>
                <td className="px-2 py-1 max-w-[160px] truncate">{r.description || "—"}</td>
                <td className="px-2 py-1 text-right">{r.quantity ?? "—"}</td>
                <td className="px-2 py-1 text-right">{r.unit_price ?? "—"}</td>
                <td className="px-2 py-1 text-right font-medium">{r.amount ?? "—"}</td>
                <td className="px-2 py-1">
                  {taxCodes.length > 0 ? (
                    <select
                      value={r.tax_code || ""}
                      onChange={(e) => setTax(i, e.target.value)}
                      className="min-w-[130px] border border-gray-200 focus:border-[#0020C2] outline-none px-1 py-0.5 text-[11px] bg-white"
                    >
                      <option value="">— derive from PO —</option>
                      {taxCodes.map((t) => (
                        <option key={t.code} value={t.code}>{t.code} ({t.rate}%)</option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-[11px] text-gray-400">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {fieldUi.matched === false && (
        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          This invoice didn't fully match the PO{fieldUi.variance != null ? ` (variance ${fieldUi.variance})` : ""}. Review before creating.
        </div>
      )}

      <button
        onClick={submit}
        className="w-full bg-[#0020C2] text-white py-2 rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-[#001AA3]"
      >
        Confirm lines &amp; tax
      </button>
    </div>
  );
}

/* ── Chat bubble ── */
function ChatBubble({ msg, sessionId, onOptionClick, onMultiSubmit, isSupplierChat, onInvoiceAttach, onInvoiceSkipAttach }) {
  const { openAgent } = useErp();
  const isUser = msg.role === "user";
  const fieldUi = !isUser && !msg.loading ? msg.result?.next_field_ui : null;
  // Local toggles so the BU / supplier browsers open INLINE on click (like RFP),
  // with no backend round-trip.
  const [showBuBrowser, setShowBuBrowser] = React.useState(false);
  const [showSupBrowser, setShowSupBrowser] = React.useState(false);

  return (
    <div className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
      {!isUser && (
        <div className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-white bg-[#0020C2]">
          <Sparkle size={11} weight="fill" />
        </div>
      )}

      <div className={`text-sm leading-relaxed
        ${isUser
          ? "max-w-[82%] rounded-2xl px-3.5 py-2 bg-[#0020C2] text-white rounded-tr-sm"
          : "flex-1 min-w-0 text-gray-900 pt-0.5"}`}>

        {msg.loading && (
          <div className="flex items-center gap-1 py-0.5">
            {[0, 150, 300].map((d) => (
              <span key={d} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
                style={{ animationDelay: `${d}ms` }} />
            ))}
          </div>
        )}

        {!msg.loading && msg.text && (
          isUser ? (
            <p className="whitespace-pre-wrap">{msg.text}</p>
          ) : openAgent === "finance-variance" ? (
            <div
              className="
        prose prose-sm max-w-none prose-p:my-2 prose-h2:mt-4 prose-h2:mb-2 prose-ul:my-2
        [&_table]:w-1/3 [&_table]:border [&_table]:border-collapse [&_table]:my-3
        [&_thead]:bg-gray-100
        [&_th]:border [&_th]:border-gray-300 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold
        [&_td]:border [&_td]:border-gray-300 [&_td]:px-3 [&_td]:py-2
        [&_tr:nth-child(even)]:bg-gray-50
      "
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(msg.text),
              }}
            />
          ) : (
            <ChatMarkdown>{msg.text}</ChatMarkdown>
          )
        )}

        {/* "Show all business units" → opens the browser INLINE on click. */}
        {!isUser && !msg.loading && openAgent === "invoice-create" &&
         /business unit/i.test(msg.text || "") && /which|what|resolved|provide|tell me|let me know|for this invoice/i.test(msg.text || "") &&
         !/purchase order|\bpo\b|po-\d|matched to|3-way|2-way|receipt/i.test(msg.text || "") &&
         !fieldUi && (
          !showBuBrowser ? (
            <button
              onClick={() => setShowBuBrowser(true)}
              className="mt-1 text-[11px] text-[#0020C2] underline hover:text-[#001AA3] transition-colors inline-flex items-center gap-1">
              <Sparkle size={9} weight="fill" /> Show all business units
            </button>
          ) : (
            <div className="widget-expand">
              <InvoiceBusinessUnitBrowser onPick={(name) => onOptionClick?.({ value: name })} />
            </div>
          )
        )}

        {/* "Show all suppliers" → opens the browser INLINE on click. */}
        {!isUser && !msg.loading && openAgent === "invoice-create" &&
         /supplier/i.test(msg.text || "") && /which|what|provide|name|tell me|let me know|for this invoice/i.test(msg.text || "") &&
         !/purchase order|\bpo\b|po-\d|matched to|3-way|2-way|receipt/i.test(msg.text || "") &&
         !fieldUi && (
          !showSupBrowser ? (
            <button
              onClick={() => setShowSupBrowser(true)}
              className="mt-1 text-[11px] text-[#0020C2] underline hover:text-[#001AA3] transition-colors inline-flex items-center gap-1">
              <Sparkle size={9} weight="fill" /> Show all suppliers
            </button>
          ) : (
            <div className="widget-expand">
              <InvoiceSupplierBrowser onPick={(name) => onOptionClick?.({ value: name })} />
            </div>
          )
        )}

        {fieldUi?.uiType === "multi_select" && (
          <MultiSelectChatOptions fieldUi={fieldUi} onSubmit={(values) => onMultiSubmit?.(values)} />
        )}
        {fieldUi?.uiType === 'attachment_upload' && (
          <ReceiptAttachmentUpload fieldUi={fieldUi} sessionId={sessionId} onUploaded={onOptionClick} />
        )}

        {fieldUi?.uiType === "attachment_gate" && (
          <div className="mt-2 rounded-lg border border-[#0020C2]/20 overflow-hidden bg-white" data-testid="chat-attachment-gate">
            <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50 border-b border-gray-100">
              {fieldUi.uploadedCount > 0
                ? `${fieldUi.uploadedCount} file${fieldUi.uploadedCount > 1 ? "s" : ""} uploaded`
                : "No files uploaded yet — use the 📎 attach button"}
            </div>
            {fieldUi.uploadedCount > 0 && fieldUi.options?.length > 0 && (
              <button
                onClick={() => onOptionClick?.(fieldUi.options[0])}
                data-testid="attachment-done-btn"
                className="w-full text-xs font-bold px-3 py-2 bg-[#0020C2] text-white hover:bg-[#001AA3] transition-colors"
              >
                Done — proceed with {fieldUi.uploadedCount} file{fieldUi.uploadedCount > 1 ? "s" : ""}
              </button>
            )}
          </div>
        )}

        {fieldUi?.uiType === "searchable_dropdown" && (
          <SearchableChatOptions fieldUi={fieldUi} onPick={(opt) => onOptionClick?.(opt)} />
        )}

        {fieldUi?.uiType === "editable_recommendations" && (
          <EditableRecommendations fieldUi={fieldUi} onSubmit={(text) => onOptionClick?.({ value: text })} />
        )}

        {fieldUi?.uiType === "line_items_builder" && (
          <LineItemsBuilder fieldUi={fieldUi} onSubmit={(text) => onOptionClick?.({ value: text })} />
        )}

        {fieldUi?.uiType === "po_line_review" && (
          <PoLineReview fieldUi={fieldUi} onSubmit={(text) => onOptionClick?.({ value: text })} />
        )}

        {fieldUi?.uiType === "business_unit_browser" && (
          <>
            {fieldUi.options?.length > 0 && (
              <div className="mt-2 flex flex-col gap-1" data-testid="chat-bu-quickpick">
                {fieldUi.options.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => onOptionClick?.(opt)}
                    className="text-xs px-3 py-1.5 border border-[#0020C2]/30 rounded-full text-[#0020C2]
                      bg-[#0020C2]/5 hover:bg-[#0020C2]/10 transition-colors text-left">
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
            <InvoiceBusinessUnitBrowser onPick={(name) => onOptionClick?.({ value: name })} />
          </>
        )}

        {fieldUi?.uiType === "supplier_browser" && (
          <InvoiceSupplierBrowser onPick={(name) => onOptionClick?.({ value: name })} />
        )}

        {fieldUi?.uiType === "invoice_attachment_upload" && (
          <InvoiceAttachmentUpload
            onUpload={(fileList) => onInvoiceAttach?.(fileList)}
            onSkip={() => onInvoiceSkipAttach?.()}
            busy={msg.attachBusy}
          />
        )}

        {fieldUi && !["searchable_dropdown", "editable_recommendations", "line_items_builder", "multi_select", "attachment_gate", "attachment_upload", "invoice_attachment_upload", "business_unit_browser", "supplier_browser", "po_line_review"].includes(fieldUi.uiType) && fieldUi.options?.length > 0 && (
          <div
            className={fieldUi.uiType === "dropdown" ? "mt-2 flex flex-col gap-1" : "mt-2 flex flex-wrap gap-1.5"}
            data-testid={`chat-field-ui-${fieldUi.field}`}
          >
            {fieldUi.options.map((opt) => (
              <button
                key={opt.value}
                onClick={() => onOptionClick?.(opt)}
                className="text-xs px-3 py-1.5 border border-[#0020C2]/30 rounded-full text-[#0020C2] bg-[#0020C2]/5 hover:bg-[#0020C2]/10 transition-colors text-left"
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {!msg.loading && msg.result && (
          <div className="mt-2 space-y-2">
            {(() => {
              const s = (msg.result.status || "").toLowerCase();
              const isOk = s === "ok" || s === "success" || s === "completed";
              const isErr = s === "oracle_error" || s === "llm_error" || s === "failed" || s === "unconfigured";
              if (!isOk && !isErr) return null;
              return (
                <div className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider 
                                    ${isOk ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-700"}`}>
                  {msg.result.http_status ? ` · HTTP ${msg.result.http_status}` : ""}
                </div>
              );
            })()}

            {msg.result.widget?.type === "ageing_dashboard" && (
              <AgeingDashboard widget={msg.result.widget} />
            )}

            {msg.result.lov?.kind && (
              <SupplierAgeingLov
                kind={msg.result.lov.kind}
                onPick={(name) => onOptionClick?.({
                  value: msg.result.lov.kind === "business_unit"
                    ? `Age this business unit: ${name}`
                    : `Age this supplier: ${name}`,
                })}
              />
            )}

            {msg.result.download?.url && (
              <button
                onClick={async () => {
                  try {
                    const resp = await api.get(msg.result.download.url, { responseType: "blob" });
                    const blobUrl = window.URL.createObjectURL(resp.data);
                    const a = document.createElement("a");
                    a.href = blobUrl;
                    a.download = msg.result.download.filename || "report";
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(blobUrl);
                  } catch (e) {
                    window.alert(`Download failed: ${e?.response?.status === 404
                      ? "the report expired — please regenerate it."
                      : (e?.message || "unknown error")}`);
                  }
                }}
                className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider
                  border border-[#0020C2] text-[#0020C2] bg-[#0020C2]/5 px-3 py-1.5 rounded-lg
                  hover:bg-[#0020C2] hover:text-white transition-colors">
                <CheckCircle size={12} weight="fill" />
                Download {String(msg.result.download.format || "").toUpperCase()} report
              </button>
            )}

            {msg.result.insights?.headline && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#0020C2] mb-1 flex items-center gap-1">
                  <Sparkle size={10} weight="fill" /> AI Insights
                </div>
                <div className="text-xs font-semibold text-gray-900 mb-1">{msg.result.insights.headline}</div>
                {msg.result.insights.bullets?.length > 0 && (
                  <ul className="text-[11px] text-gray-700 space-y-0.5 list-disc pl-4">
                    {msg.result.insights.bullets.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                )}
              </div>
            )}

            {msg.result.oracle_response && !isSupplierChat && (
              <details>
                <summary className="text-[10px] font-bold uppercase tracking-wider text-gray-500 cursor-pointer hover:text-gray-700">
                  Oracle response ↓
                </summary>
                <pre className="text-[10px] font-mono bg-gray-50 border border-gray-200 rounded p-2 mt-1 overflow-x-auto max-h-28">
                  {JSON.stringify(msg.result.oracle_response, null, 2).slice(0, 1200)}
                </pre>
              </details>
            )}

            {["ok", "success"].includes((msg.result.status || "").toLowerCase()) && msg.result.parsed_payload && msg.onSave && (
              <button onClick={msg.onSave}
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider
                  border border-amber-300 text-amber-700 bg-amber-50 px-2 py-0.5 hover:bg-amber-100 rounded-full">
                <Star size={9} weight="fill" /> Save query
              </button>
            )}
          </div>
        )}

        {msg.timestamp && (
          <div className={`text-[9px] mt-1 opacity-40 ${isUser ? "text-right" : "text-left"}`}>
            {msg.timestamp}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Saved queries block ── */
function SavedQueries({
  saved, schedsFor, busy, openSchedFor, setOpenSchedFor,
  schedForm, setSchedForm, replay, removeSaved, createSchedule, toggleSchedule, deleteSchedule,
}) {
  if (!saved.length) return null;
  return (
    <div data-testid="agent-saved-queries" className="border border-gray-200 bg-amber-50 p-3">
      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-600 mb-2 flex items-center gap-2">
        <Star size={12} weight="fill" className="text-amber-500" /> Saved · {saved.length}
      </div>
      <div className="space-y-1.5">
        {saved.map((s) => {
          const ss = schedsFor(s.id);
          return (
            <div key={s.id} className="bg-white border border-gray-200" data-testid={`saved-${s.id}`}>
              <div className="flex items-center gap-2 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold truncate">{s.label}</div>
                  <div className="text-[10px] text-gray-500 font-mono">
                    {s.run_count > 0 ? `Run ${s.run_count}×` : "Never replayed"}
                  </div>
                </div>
                <button
                  onClick={() => setOpenSchedFor(openSchedFor === s.id ? null : s.id)}
                  className="text-gray-500 hover:text-[#0020C2] text-[10px] font-bold inline-flex items-center gap-1 shrink-0">
                  <Calendar size={11} weight="bold" /> Sched
                </button>
                <button
                  onClick={() => replay(s.id)}
                  disabled={busy}
                  data-testid={`agent-replay-${s.id}`}
                  className="text-[#0020C2] text-[10px] font-bold inline-flex items-center gap-1 shrink-0 disabled:opacity-40 hover:underline">
                  <Lightning size={11} weight="bold" /> Replay
                </button>
                <button onClick={() => removeSaved(s.id)} className="text-[#DC2626] shrink-0">
                  <Trash size={11} weight="bold" />
                </button>
              </div>

              {ss.length > 0 && (
                <div className="border-t border-gray-100 px-3 py-1.5 space-y-1 bg-gray-50">
                  {ss.map((sch) => (
                    <div key={sch.id} className="flex items-center gap-2 text-[10px]">
                      <span className="font-mono font-bold text-gray-700 truncate flex-1">{describeSchedule(sch)}</span>
                      <span className={`px-1 py-0.5 text-[8px] font-bold uppercase rounded
                        ${sch.enabled ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-500"}`}>
                        {sch.enabled ? "On" : "Off"}
                      </span>
                      <button onClick={() => toggleSchedule(sch.id)} className="text-gray-400 hover:text-[#0020C2]">
                        {sch.enabled ? <ToggleRight size={14} weight="fill" /> : <ToggleLeft size={14} />}
                      </button>
                      <button onClick={() => deleteSchedule(sch.id)} className="text-[#DC2626]">
                        <Trash size={10} weight="bold" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {openSchedFor === s.id && (
                <div className="border-t border-gray-100 px-3 py-2 bg-blue-50 space-y-2">
                  <div className="grid grid-cols-2 gap-1.5">
                    <select
                      value={schedForm.cadence}
                      onChange={(e) => setSchedForm({ ...schedForm, cadence: e.target.value })}
                      className="border border-gray-300 px-1.5 py-1 text-[10px] col-span-2">
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                    {schedForm.cadence === "weekly" && (
                      <select
                        value={schedForm.day_of_week}
                        onChange={(e) => setSchedForm({ ...schedForm, day_of_week: parseInt(e.target.value) })}
                        className="border border-gray-300 px-1.5 py-1 text-[10px]">
                        {DOW.map((d, i) => <option key={d} value={i}>{d}</option>)}
                      </select>
                    )}
                    {schedForm.cadence === "monthly" && (
                      <select
                        value={schedForm.day_of_month}
                        onChange={(e) => setSchedForm({ ...schedForm, day_of_month: parseInt(e.target.value) })}
                        className="border border-gray-300 px-1.5 py-1 text-[10px]">
                        {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                          <option key={d} value={d}>Day {d}</option>
                        ))}
                      </select>
                    )}
                    <select
                      value={schedForm.hour_utc}
                      onChange={(e) => setSchedForm({ ...schedForm, hour_utc: parseInt(e.target.value) })}
                      className="border border-gray-300 px-1.5 py-1 text-[10px] font-mono">
                      {Array.from({ length: 24 }, (_, i) => i).map((h) => (
                        <option key={h} value={h}>{String(h).padStart(2, "0")}:00 UTC</option>
                      ))}
                    </select>
                    <button
                      onClick={() => createSchedule(s.id)}
                      className="bg-[#0020C2] text-white py-1 text-[10px] font-bold uppercase tracking-wider col-span-2">
                      Save Schedule
                    </button>
                  </div>
                  <button
                    onClick={() => setOpenSchedFor(null)}
                    className="text-[10px] text-gray-500 hover:text-gray-700 w-full text-center">
                    Cancel
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── PO Decision Card ── */
function PODecisionCard({ onYes, onSkip }) {
  const [showInput, setShowInput] = React.useState(false);
  const [poInput, setPoInput] = React.useState("");

  return (
    <div className="widget-expand rounded-2xl overflow-hidden bg-white border border-gray-200 shadow-sm">
      {/* Accent header strip */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#0020C2]/[0.06] to-transparent border-b border-gray-100">
        <div className="w-6 h-6 rounded-lg bg-[#0020C2] flex items-center justify-center shrink-0">
          <Lightning size={12} weight="fill" className="text-white" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#0020C2]">
            Purchase Order
          </div>
        </div>
      </div>

      <div className="p-4">
        <p className="text-sm font-bold text-gray-900 mb-1">
          Do you have a PO number for this invoice?
        </p>
        <p className="text-xs text-gray-500 leading-relaxed mb-4">
          PO invoices are matched to an existing purchase order in Oracle.
          Choose <span className="font-medium text-gray-600">standalone</span> to
          create the invoice without one.
        </p>

        {!showInput ? (
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowInput(true)}
              className="group flex-1 min-w-[150px] flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl
                border border-[#0020C2]/25 bg-[#0020C2]/[0.04] hover:bg-[#0020C2]/[0.08]
                hover:border-[#0020C2]/40 transition-all text-left">
              <div className="w-7 h-7 rounded-lg bg-[#0020C2]/10 group-hover:bg-[#0020C2]/15
                flex items-center justify-center shrink-0 transition-colors">
                <CheckCircle size={15} weight="fill" className="text-[#0020C2]" />
              </div>
              <div className="min-w-0">
                <div className="text-xs font-bold text-[#0020C2]">Yes, I have a PO</div>
                <div className="text-[10px] text-[#0020C2]/60">Match to a purchase order</div>
              </div>
            </button>

            <button
              onClick={onSkip}
              className="group flex-1 min-w-[150px] flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl
                border border-gray-200 bg-gray-50 hover:bg-gray-100 hover:border-gray-300
                transition-all text-left">
              <div className="w-7 h-7 rounded-lg bg-gray-200/60 group-hover:bg-gray-300/60
                flex items-center justify-center shrink-0 transition-colors">
                <PaperPlaneRight size={14} weight="bold" className="text-gray-500" />
              </div>
              <div className="min-w-0">
                <div className="text-xs font-bold text-gray-700">Standalone</div>
                <div className="text-[10px] text-gray-400">Create without a PO</div>
              </div>
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
              Purchase order number
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={poInput}
                onChange={(e) => setPoInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && poInput.trim()) onYes(poInput.trim()); }}
                placeholder="e.g. PO-2024-001"
                autoFocus
                className="flex-1 px-3.5 py-2.5 text-sm rounded-xl border border-gray-300 font-mono
                  focus:outline-none focus:border-[#0020C2] focus:ring-2 focus:ring-blue-100 transition-all"
              />
              <button
                onClick={() => poInput.trim() && onYes(poInput.trim())}
                disabled={!poInput.trim()}
                className="px-4 py-2.5 rounded-xl text-xs font-bold text-white whitespace-nowrap
                  bg-[#0020C2] hover:bg-[#001AA3] disabled:opacity-40 transition-all
                  flex items-center gap-1.5">
                Continue <PaperPlaneRight size={12} weight="fill" />
              </button>
            </div>
            <button
              onClick={() => { setShowInput(false); setPoInput(""); }}
              className="text-[10px] text-gray-400 hover:text-gray-600 transition-colors">
              ← Back
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Progress + quick actions ── */
function ProgressBar({ progress }) {
  if (!progress) return null;
  const pctMatch = progress.match(/\((\d+)%\)/);
  const stepMatch = progress.match(/(\d+)\/(\d+)/);
  if (!pctMatch || !stepMatch) return null;
  const pct = parseInt(pctMatch[1], 10);
  const [, done, total] = stepMatch;
  return (
    <div className="px-4 pt-3 pb-1" data-testid="copilot-progress">
      <div className="flex justify-between text-xs text-gray-500 mb-1">
        <span>Step {done} of {total}</span>
        <span>{pct}% complete</span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full bg-[#0020C2] transition-all duration-300" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

const SUPPLIER_QUICK_ACTIONS = [
  "Create Supplier", "Check Supplier Status", "Continue Draft",
  "Supplier Guidelines", "Contact Support",
];

function QuickActions({ onPick }) {
  return (
    <div className="flex gap-2 flex-wrap px-4 pb-2" data-testid="copilot-quick-actions">
      {SUPPLIER_QUICK_ACTIONS.map((a) => (
        <button
          key={a}
          onClick={() => onPick(a)}
          className="text-xs px-3 py-1 border border-gray-200 rounded-full text-gray-600 hover:border-[#0020C2] hover:text-[#0020C2] transition-colors"
        >
          {a}
        </button>
      ))}
    </div>
  );
}

function CapturedFieldsPanel({ fields }) {
  const entries = Object.entries(fields || {});
  if (!entries.length) return null;
  return (
    <details className="mx-4 mb-2 border border-gray-100 rounded-lg" data-testid="copilot-captured-fields">
      <summary className="cursor-pointer text-xs font-bold text-gray-500 px-3 py-2">
        Captured so far ({entries.length})
      </summary>
      <div className="px-3 pb-2 space-y-1">
        {entries.map(([field, value]) => (
          <div key={field} className="flex justify-between text-xs border-t border-gray-50 pt-1">
            <span className="text-gray-400">{field.replace(/_/g, " ")}</span>
            <span className="font-mono text-gray-700">{String(value)}</span>
          </div>
        ))}
      </div>
    </details>
  );
}

function InvoiceConfirmPopup({ text, onConfirm, onCancel, isSupplier = false }) {
  const extractFields = (t) => {
    const clean = t.replace(/\*\*/g, "");
    const fields = [];

    const amt = clean.match(/(?:Amount|Total)[^\d]*([\d,]+\.?\d*)/i);
    const cur = clean.match(/\b(AED|USD|EUR|GBP)\b/);
    if (amt) fields.push({ label: "Invoice Amount", value: (cur ? cur[0] + " " : "") + amt[1], highlight: true });

    const buMatch = clean.match(/Business Unit[:\s]*((?:[^\n]){1,60})/i);
    if (buMatch?.[1]?.trim()) fields.push({ label: "Business Unit", value: buMatch[1].trim().replace(/^[-:]\s*/, "") });

    for (const k of ["Supplier:", "Supplier Name", "Supplier"]) {
      const m = clean.match(new RegExp(k + "[:\\s]*((?:[^\\n]){1,60})", "i"));
      if (m?.[1]?.trim()) { fields.push({ label: "Supplier", value: m[1].trim().replace(/^[-:]\s*/, "") }); break; }
    }

    const pt = clean.match(/Payment Terms[:\s]*((?:[^\n]){1,60})/i);
    if (pt?.[1]?.trim()) fields.push({ label: "Payment Terms", value: pt[1].trim().replace(/^[-:]\s*/, "") });

    const inv = clean.match(/Invoice (?:Number|No)[:\s]*((?:[^\n]){1,60})/i);
    if (inv?.[1]?.trim()) fields.push({ label: "Invoice Number", value: inv[1].trim().replace(/^[-:]\s*/, "") });

    const dt = clean.match(/Invoice Date[:\s]*((?:[^\n]){1,60})/i);
    if (dt?.[1]?.trim()) fields.push({ label: "Invoice Date", value: dt[1].trim().replace(/^[-:]\s*/, "") });

    const desc = clean.match(/Description[:\s]*((?:[^\n]){1,60})/i);
    if (desc?.[1]?.trim()) fields.push({ label: "Description", value: desc[1].trim().replace(/^[-:]\s*/, "") });

    return fields.length ? fields : [{ label: "Details", value: "Review the chat above" }];
  };

  const fields = extractFields(text);

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[9999]"
      style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)" }}>
      <div className="bg-white rounded-2xl p-6 w-full mx-4 shadow-2xl"
        style={{ maxWidth: 500, border: "1px solid #E5E7EB" }}>

        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 rounded-full bg-[#0020C2] flex items-center justify-center shrink-0">
            <CheckCircle size={18} weight="fill" color="white" />
          </div>
          <div>
            <p className="font-bold text-gray-900 text-sm">{isSupplier ? "Review Supplier" : "Review Invoice"}</p>
            <p className="text-xs text-gray-500">{isSupplier ? "Confirm all supplier details before creating in Oracle Fusion" : "Confirm all details before creating in Oracle Fusion"}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2.5 mb-5">
          {fields.map((f, i) => (
            <div key={i}
              style={{
                gridColumn: f.highlight ? "1 / -1" : "auto",
                background: f.highlight ? "rgba(16,185,129,0.07)" : "#F9FAFB",
                border: f.highlight ? "1px solid rgba(16,185,129,0.25)" : "1px solid #E5E7EB",
                borderRadius: 10,
                padding: "9px 12px",
              }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>
                {f.label}
              </p>
              <p style={{ fontSize: f.highlight ? 15 : 13, fontWeight: f.highlight ? 700 : 500, color: f.highlight ? "#059669" : "#111827", wordBreak: "break-word" }}>
                {f.value}
              </p>
            </div>
          ))}
        </div>

        <div className="flex gap-3">
          <button onClick={onConfirm}
            className="flex-1 py-2.5 rounded-xl font-bold text-white text-sm transition-all hover:opacity-90"
            style={{ background: "linear-gradient(135deg, #10B981, #059669)" }}>
            ✓ {isSupplier ? "Create Supplier" : "Create Invoice"}
          </button>
          <button onClick={onCancel}
            className="flex-1 py-2.5 rounded-xl font-semibold text-sm transition-all hover:bg-gray-100"
            style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", color: "#6B7280" }}>
            ✕ Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Input bar ── */
function InputBar({ inputRef, value, onChange, onKeyDown, onSend, busy, placeholder, onAttach, attaching, attachedCount, accept, title }) {
  const fileInputRef = React.useRef(null);
  return (
    <div className="border-t border-gray-200 px-4 py-3 bg-white shrink-0">
      <div className="flex items-end gap-2 border border-gray-300 bg-gray-50
        focus-within:border-[#0020C2] focus-within:ring-2 focus-within:ring-blue-100">
        {onAttach && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept={accept || ".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"}
              multiple
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length) onAttach(files);
                e.target.value = "";
              }}
              data-testid="agent-attach-input"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={attaching}
              title={title || "Attach a file (PDF, DOCX, XLSX, PNG, JPG)"}
              data-testid="agent-attach-button"
              className="m-2 w-8 h-8 flex items-center justify-center text-gray-500 hover:text-[#0020C2]
                hover:bg-gray-100 rounded transition-colors shrink-0 disabled:opacity-40 relative">
              {attaching
                ? <span className="w-3 h-3 border-2 border-gray-300 border-t-[#0020C2] rounded-full animate-spin" />
                : <Paperclip size={16} weight="bold" />}
              {attachedCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#0020C2] text-white text-[9px] rounded-full flex items-center justify-center">
                  {attachedCount}
                </span>
              )}
            </button>
          </>
        )}

        <textarea
          ref={inputRef}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          rows={1}
          disabled={busy}
          placeholder={placeholder}
          data-testid="agent-prompt"
          className="flex-1 bg-transparent px-3 py-2.5 text-sm resize-none
            outline-none placeholder-gray-400 font-mono disabled:opacity-50 leading-relaxed"
          style={{ minHeight: "2.5rem", maxHeight: "7.5rem" }}
        />
        <button
          onClick={onSend}
          disabled={busy || !value.trim()}
          className="m-2 w-8 h-8 bg-[#0020C2] text-white flex items-center justify-center
            hover:bg-[#001AA3] disabled:opacity-40 transition-colors shrink-0">
          {busy
            ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            : <PaperPlaneRight size={14} weight="fill" />}
        </button>
      </div>
      <span className="text-[10px] text-gray-400 mt-1 block">↵ send · shift+↵ new line</span>
    </div>
  );
}

/* ── Chat history control ── */
function ChatHistoryControl({ agentKey, currentSessionId, onNewChat, onOpenSession }) {
  const [open, setOpen] = React.useState(false);
  const [sessions, setSessions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const wrapRef = React.useRef(null);

  const load = React.useCallback(async () => {
    if (!agentKey) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/erp-agents/${agentKey}/sessions`);
      setSessions(Array.isArray(data) ? data : []);
    } catch {
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [agentKey]);

  React.useEffect(() => { if (open) load(); }, [open, load]);

  React.useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this conversation?")) return;
    try {
      await api.delete(`/erp-agents/${agentKey}/sessions/${sid}`);
      load();
    } catch { /* ignore */ }
  };

  return (
    <div className="flex items-center gap-3" ref={wrapRef}>
      <button
        onClick={onNewChat}
        className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2]
          flex items-center gap-1 shrink-0"
        data-testid="chat-new"
        title="Start a new conversation">
        <Plus size={13} weight="bold" /> New Chat
      </button>

      <div className="relative">
        <button
          onClick={() => setOpen((v) => !v)}
          className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2]
            flex items-center gap-1"
          data-testid="chat-history-toggle"
          title="Chat history">
          History
          <CaretDown size={10} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {open && (
          <div className="absolute right-0 z-40 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg
            max-h-80 overflow-y-auto" data-testid="chat-history-list">
            {loading && <div className="px-3 py-2 text-xs text-gray-400">Loading…</div>}
            {!loading && sessions.length === 0 && (
              <div className="px-3 py-2 text-xs text-gray-400">No previous conversations.</div>
            )}
            {sessions.map((s) => (
              <div
                key={s.session_id}
                onClick={() => { onOpenSession(s.session_id); setOpen(false); }}
                className={`px-3 py-2 text-xs cursor-pointer border-b border-gray-50 last:border-0
                  hover:bg-[#0020C2]/5 flex items-center gap-2
                  ${s.session_id === currentSessionId ? "bg-[#0020C2]/5" : ""}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-gray-800 truncate">{s.title}</div>
                  <div className="text-[10px] text-gray-400 flex items-center gap-1.5">
                    <span className={`px-1 py-0.5 rounded text-[8px] font-bold uppercase
                      ${s.status === "completed" ? "bg-emerald-100 text-emerald-700"
                        : s.status === "cancelled" ? "bg-gray-200 text-gray-500"
                        : "bg-blue-100 text-blue-700"}`}>
                      {s.status}
                    </span>
                    {s.updated_at ? new Date(s.updated_at).toLocaleString([], {
                      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                    }) : ""}
                  </div>
                </div>
                <button
                  onClick={(e) => deleteSession(s.session_id, e)}
                  className="text-[#DC2626] shrink-0 p-1" title="Delete">
                  <Trash size={11} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Main modal ── */
export default function AgentRunModal() {
  const { openAgent, setOpenAgent, setSetupOpen, profile, agents, launchAgent } = useErp();

  const [messages, setMessages] = React.useState([]);
  const [copilotProgress, setCopilotProgress] = React.useState(null);
  const [copilotFields, setCopilotFields] = React.useState(null);
  const isSupplierChat = openAgent === "supplier-creation" || openAgent === "supplier-create";
  const isInvoiceChat = openAgent === "invoice-create";
  // Invoice-create shows a mode picker first: 'chat' (existing flow) or
  // 'upload' (drop an invoice PDF, processed server-side). null = not chosen.
  const [invoiceMode, setInvoiceMode] = React.useState(null);
  const [invoiceUploadBusy, setInvoiceUploadBusy] = React.useState(false);
  const [invoiceUploadMsg, setInvoiceUploadMsg] = React.useState(null);
  // PDF-extracted invoice → full-screen review panel.
  const [pdfReview, setPdfReview] = React.useState(null);  // the `extracted` object, or null
  const [pdfFileUrl, setPdfFileUrl] = React.useState(null); // object URL of the uploaded PDF, for preview
  const isChatOnly = openAgent === "supplier-ageing";
  // Invoice-create is chat-only in the widget flow: hide the manual wizard,
  // chat takes the full width (like RFP's prMode). Chat-only agents too.
  const hideWizard = isChatOnly || isInvoiceChat;

  const sessionKey = isSupplierChat ? `supplier_session_id` : `agent_session_${openAgent}`;

  const [attaching, setAttaching] = React.useState(false);
  const [attachedCount, setAttachedCount] = React.useState(0);

  const [invoicePendingFiles, setInvoicePendingFiles] = React.useState([]);
  const [invoiceAttaching, setInvoiceAttaching] = React.useState(false);
  const pendingAttachmentModeRef = React.useRef(null);

  const [input, setInput] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [validating, setValidating] = React.useState(false);
  const [chatStarted, setChatStarted] = React.useState(false);
  const [sessionId, setSessionId] = React.useState(() => crypto.randomUUID());
  const HISTORY_AGENTS = ["invoice-create", "supplier-creation", "purchase-req-create", "receipt-create", "supplier-ageing"];
  const supportsHistory = HISTORY_AGENTS.includes(openAgent);

  const [fieldValues, setFieldValues] = React.useState({});
  const [missingFields, setMissingFields] = React.useState([]);
  const [fieldErrors, setFieldErrors] = React.useState({});
  const [fieldWarnings, setFieldWarnings] = React.useState({});
  const [saved, setSaved] = React.useState([]);
  const [schedules, setSchedules] = React.useState([]);
  const [savedBusy, setSavedBusy] = React.useState(false);
  const [openSchedFor, setOpenSchedFor] = React.useState(null);
  const [schedForm, setSchedForm] = React.useState({
    cadence: "monthly", day_of_week: 0, day_of_month: 1, hour_utc: 8,
  });

  const [wizardSchemaFields, setWizardSchemaFields] = React.useState([]);
  const lastSentContextRef = React.useRef("");

  const [showPOCard, setShowPOCard] = React.useState(false);
  const [poDecisionMade, setPoDecisionMade] = React.useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = React.useState(false);
  const [pendingConfirmText, setPendingConfirmText] = React.useState("");

  const [wizardBusy, setWizardBusy] = React.useState(false);
  const [wizardResult, setWizardResult] = React.useState(null);

  const agent = agents.find((a) => a.key === openAgent);
  const messagesEndRef = React.useRef(null);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const ts = () => new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const loadSaved = React.useCallback(async (key) => {
    if (!key) return;
    try {
      const [{ data: sq }, { data: sch }] = await Promise.all([
        api.get(`/erp-agents/saved-queries?agent_key=${key}`),
        api.get(`/erp-agents/schedules`),
      ]);
      setSaved(sq || []);
      setSchedules(sch || []);
    } catch {
      setSaved([]);
      setSchedules([]);
    }
  }, []);

  const handleAttachFiles = async (files) => {
    setAttaching(true);
    const results = [];
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        const { data } = await api.post(
          `/erp-agents/supplier-creation/${sessionId}/upload-attachment`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        setAttachedCount(data.attachments_count);
        results.push({ name: file.name, ok: true, count: data.attachments_count });
      } catch (e) {
        const msg = e?.response?.data?.detail || `Failed to upload ${file.name}`;
        results.push({ name: file.name, ok: false, error: msg });
      }
    }
    setAttaching(false);
    const succeeded = results.filter(r => r.ok).map(r => r.name);
    const failed = results.filter(r => !r.ok);
    let summary = "";
    if (succeeded.length) summary += `📎 Attached: ${succeeded.join(", ")}`;
    if (failed.length) summary += (summary ? "\n" : "") + failed.map(f => `❌ ${f.name}: ${f.error}`).join("\n");
    setMessages((prev) => [...prev, {
      id: Date.now(), role: "assistant", timestamp: ts(), text: summary || "Upload complete.",
    }]);
    if (succeeded.length > 0) {
      setTimeout(() => sendMessage("__refresh_attachment_ui__"), 300);
    }
  };

  const [receiptAttaching, setReceiptAttaching] = React.useState(false);
  const [receiptAttachedName, setReceiptAttachedName] = React.useState(null);

  const handleReceiptAttachFiles = async (files) => {
    const file = files[0];
    if (!file) return;
    setReceiptAttaching(true);
    setMessages((prev) => [...prev, { id: Date.now() + 1, role: 'assistant', loading: true }]);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post(
        `/erp-agents/receipt-create/${sessionId}/upload-receipt-attachment`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setReceiptAttachedName(file.name);
      const reply = data?.reply || data?.response || 'Receipt document uploaded.';
      if (data?.progress) setCopilotProgress(data.progress);
      if (data?.fields_collected) setCopilotFields(data.fields_collected);
      setMessages((prev) =>
        prev.map((m) => m.loading ? { ...m, loading: false, timestamp: ts(), text: reply, result: data } : m)
      );
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Upload failed';
      setMessages((prev) => prev.map((m) => m.loading ? { ...m, loading: false, timestamp: ts(), text: '\u274c ' + msg } : m));
    } finally {
      setReceiptAttaching(false);
    }
  };

  React.useEffect(() => {
    setMessages([]);
    setInput("");
    setBusy(false);
    setChatStarted(false);
    setFieldValues({});
    setMissingFields([]);
    setFieldErrors({});
    setFieldWarnings({});
    setOpenSchedFor(null);
    setShowPOCard(false);
    setPoDecisionMade(false);
    setShowConfirmPopup(false);
    setPendingConfirmText("");
    setWizardBusy(false);
    setWizardResult(null);
    setWizardSchemaFields([]);
    setInvoicePendingFiles([]);
    pendingAttachmentModeRef.current = null;
    lastSentContextRef.current = "";
    setInvoiceMode(null);
    setInvoiceUploadBusy(false);
    setInvoiceUploadMsg(null);
    setPdfReview(null);
    setPdfFileUrl((u) => { if (u) { try { URL.revokeObjectURL(u); } catch {} } return null; });
    if (openAgent) loadSaved(openAgent);
  }, [openAgent, loadSaved]);

  if (!openAgent) return null;
   if (openAgent === "invoice-ageing") return <InvoiceAgeingModal />;
   if (openAgent === "supplier-ageing") return <SupplierAgeingModal />;
   if (openAgent == "rfp-create") return  <RFPAgentModal />;


  const close = () => {
    setOpenAgent(null);
    setMessages([]);
    setFieldValues({});
    setChatStarted(false);
  };

  const payloadFV = () => {
  const out = {};
  for (const [k, v] of Object.entries(fieldValues)) {
    if (k.endsWith("__label")) continue;
    out[k] = Array.isArray(v) && v[0]?.attachment_id ? v.map((i) => i.attachment_id) : v;
  }
  return out;
};

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const buildAttachmentsPayload = async () => {
    const classificationFiles = fieldValues["classification_documents"] || [];
    const extraFiles = fieldValues["attachments"] || [];
    let classificationAttachmentMap = {};
    try {
      const { data } = await api.get(`/erp-agents/agent-fields/${openAgent}`);
      classificationAttachmentMap = data?.classification_attachment_map || {};
    } catch (e) {
      console.warn("[attachments] could not fetch classification_attachment_map, proceeding without auto-tagging", e);
    }
    const selectedClassifications = (fieldValues["business_classifications"] || [])
      .filter((c) => classificationAttachmentMap[c]);

    const attachments = [];
    for (let i = 0; i < classificationFiles.length; i++) {
      const file = classificationFiles[i];
      if (!(file instanceof File)) continue;
      const base64 = await fileToBase64(file);
      const code = selectedClassifications[i];
      attachments.push({
        file_name: file.name,
        content_type: file.type,
        base64_content: base64,
        classification_code: code || null,
        required_document: code ? classificationAttachmentMap[code] : null,
        category_name: code ? "Business Classifications" : "Miscellaneous",
        title: code ? classificationAttachmentMap[code] : file.name,
      });
    }
    for (const file of extraFiles) {
      if (!(file instanceof File)) continue;
      const base64 = await fileToBase64(file);
      attachments.push({
        file_name: file.name, content_type: file.type, base64_content: base64,
        classification_code: null, required_document: null,
        category_name: "Miscellaneous", title: file.name,
      });
    }
    return attachments;
  };

  const encodeInvoiceFiles = async (files) => {
    const encoded = [];
    for (const file of files) {
      const b64 = await fileToBase64(file);
      encoded.push({
        file_name: file.name,
        mime_type: file.type || "application/pdf",
        data: b64,
      });
    }
    return encoded;
  };

  const handleInvoiceWidgetUpload = async (fileList) => {
    setInvoiceAttaching(true);
    try {
      const encoded = await encodeInvoiceFiles(fileList);
      setInvoicePendingFiles(encoded);
      pendingAttachmentModeRef.current = "attach";
      setTimeout(() => sendMessage("__ATTACHMENT_ADDED__"), 50);
    } finally {
      setInvoiceAttaching(false);
    }
  };

  const handleInvoiceWidgetSkip = () => {
    sendMessage("__SKIP_ATTACHMENT__");
  };

  const handleInvoicePdfUpload = async (files) => {
    const file = files?.[0];
    if (!file) return;
    setInvoiceUploadBusy(true);
    setInvoiceUploadMsg(null);
    // Keep a local preview URL of the PDF for the split-view review panel.
    try {
      if (pdfFileUrl) URL.revokeObjectURL(pdfFileUrl);
      setPdfFileUrl(URL.createObjectURL(file));
    } catch { /* preview is best-effort */ }
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("session_id", sessionId);
      // NEW ENDPOINT — server extracts the invoice from the PDF (n8n-style flow
      // wired in phase 2). Returns { status, reply, next_field_ui, ... } like the
      // chat endpoint so the conversation can continue from the extracted data.
      const { data } = await api.post(
        "/erp-agents/invoice-create/upload-invoice-pdf",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      // Open the full-screen review panel with the extracted invoice.
      if (data?.status === "ok" && data?.extracted) {
        setPdfReview(data.extracted);
      } else {
        // Not an invoice, or an error — surface the message on the card.
        setInvoiceUploadMsg(data?.reply || data?.response || "Couldn't read that PDF.");
      }
    } catch (e) {
      setInvoiceUploadMsg(
        e?.response?.data?.detail || e?.message || "Couldn't process that PDF. Try again or use chat."
      );
    } finally {
      setInvoiceUploadBusy(false);
    }
  };

  const dryRunValidate = async () => {
    setValidating(true);
    setFieldErrors({});
    setFieldWarnings({});
    if (!chatStarted) setChatStarted(true);
    try {
      const { data } = await api.post(`/erp-agents/${openAgent}/validate`, {
        field_values: payloadFV(),
      });
      setFieldErrors(data.field_errors || {});
      setFieldWarnings(data.field_warnings || {});
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          role: "assistant",
          timestamp: ts(),
          text: data.ok
            ? "✅ Validation passed — all fields look good. Ready to send to Oracle."
            : `❌ ${Object.keys(data.field_errors || {}).length} error(s) found — check the highlighted fields in the wizard.`,
        },
      ]);
    } catch (e) {
      setFieldErrors({ _general: e?.response?.data?.detail || e.message });
    } finally {
      setValidating(false);
    }
  };

  const sendMessage = async (text, forceRun = false) => {
    const rawText = (text || "").trim();
    if (!rawText || busy) return;
    const fullCtx = isChatOnlyAgent(openAgent) ? buildFieldContext(fieldValues, wizardSchemaFields) : "";
    const ctx = fullCtx && fullCtx !== lastSentContextRef.current ? fullCtx : "";
    const userText = ctx ? `${ctx}. ${rawText}` : rawText;
    if (fullCtx) lastSentContextRef.current = fullCtx;

    setInput("");
    if (inputRef.current) inputRef.current.style.height = "auto";
    if (!chatStarted) setChatStarted(true);

    const isSentinel = [
      "__ATTACHMENT_ADDED__", "__SKIP_ATTACHMENT__",
      "__refresh_attachment_ui__",
    ].includes(rawText);

    const now = ts();
    setMessages((prev) => [
      ...prev,
      ...(isSentinel ? [] : [{ id: Date.now(), role: "user", text: userText, timestamp: now }]),
      { id: Date.now() + 1, role: "assistant", loading: true },
    ]);
    setBusy(true);
    setMissingFields([]);
    setFieldErrors({});
    setFieldWarnings({});

    try {
      let data;

      if (openAgent === "invoice-create") {
        const body = {
          session_id: sessionId,
          message: userText,
        };
        if (invoicePendingFiles.length) {
          body.attachments = invoicePendingFiles;
          body.attachment_mode = pendingAttachmentModeRef.current || null;
        }
        const res = await api.post("/erp-agents/invoice-create/chat", body);
        data = res.data;
        if (invoicePendingFiles.length) setInvoicePendingFiles([]);
        pendingAttachmentModeRef.current = null;
      } else if (openAgent === "purchase-req-create") {
        const res = await api.post("/erp-agents/purchase-req-create/chat", {
          session_id: sessionId,
          message: userText,
        });
        data = res.data;
      } else if (openAgent === "receipt-create") {
        const res = await api.post("/erp-agents/receipt-create/chat", {
          session_id: sessionId,
          message: userText,
        });
        data = res.data;
        if (data?.progress) setCopilotProgress(data.progress);
        if (data?.fields_collected) setCopilotFields(data.fields_collected);
      }
      else if (openAgent === "supplier-ageing") {
        const res = await api.post("/erp-agents/supplier-ageing/chat", {
          session_id: sessionId,
          message: userText,
        });
        data = res.data;
        if (data?.progress) setCopilotProgress(data.progress);
        if (data?.fields_collected) setCopilotFields(data.fields_collected);
      }
      else if (openAgent === "supplier-creation" || openAgent === "supplier-create") {
        const res = await api.post("/erp-agents/supplier-creation/chat", {
          session_id: sessionId,
          message:    userText,
        });
        data = res.data;
      }
      else if (openAgent === "account-overview" ||
        openAgent === "finance-account-overview" ||
        openAgent?.includes("account-overview") ||
        openAgent?.includes("account_overview")) {
        const res = await api.post("/erp-agents/account-overview/chat", {
          session_id: sessionId,
          question: userText,
        });
        data = {
          status: "success",
          response: res.data?.answer || res.data?.response || JSON.stringify(res.data),
        };
      }
      else if (openAgent === "finance-variance" ||
        openAgent === "finance-account-variance" ||
        openAgent?.includes("finance-variance")) {
        const res = await api.post("/erp-agents/finance-variance/chat", {
          session_id: sessionId,
          question: userText,
        });
        data = {
          status: "success",
          response: res.data?.answer || res.data?.response || JSON.stringify(res.data),
        };
      }
      else if (
        openAgent === "finance-future-insight" ||
        openAgent === "future-insight" ||
        openAgent?.includes("finance-future")
      ) {
        const res = await api.post("/erp-agents/future-insights/chat", {
          session_id: sessionId,
          question: userText,
        });
        data = {
          status:   "success",
          response: res.data?.answer || res.data?.response || JSON.stringify(res.data),
        };
      }
      else if (
        openAgent === "finance-narrative" ||
        openAgent === "finance-reporting-narrative" ||
        openAgent?.includes("finance-narrative")
      ) {
        const res = await api.post("/erp-agents/future-insights/chat", {
          session_id: sessionId,
          question: userText,
        });
        data = {
          status:   "success",
          response: res.data?.answer || res.data?.response || JSON.stringify(res.data),
        };
      }
      else {
        const res = await api.post(`/erp-agents/${openAgent}/run`, {
          prompt: userText,
          field_values: payloadFV(),
          force_run: forceRun,
        });
        data = res.data;

        if (data?.parsed_payload && typeof data.parsed_payload === "object")
          setFieldValues((prev) => ({ ...prev, ...data.parsed_payload }));
        if (data?.missing?.length)
          setMissingFields(data.missing);
      }

      const status = (data?.status || "").toLowerCase();
      const backendMsg = data?.reply || data?.response || data?.message;

      if (data?.progress !== undefined) setCopilotProgress(data.progress);
      if (data?.fields_collected !== undefined) {
        setCopilotFields(data.fields_collected);
        if (isSupplierChat && data.fields_collected && typeof data.fields_collected === "object") {
          const CHAT_TO_WIZARD = {
            supplier_city:    "city",
            supplier_country: "country",
            supplier_state:   "state",
            supplier_site:    "supplier_site",
          };
          setFieldValues(prev => {
            const merged = { ...prev };
            for (const [chatKey, val] of Object.entries(data.fields_collected)) {
              if (val === null || val === undefined || val === "") continue;
              const wizardKey = CHAT_TO_WIZARD[chatKey] || chatKey;
              const wizardVal = (chatKey === "procurement_bus")
                ? (Array.isArray(val) ? val[0] : val)
                : val;
              if (wizardVal && !merged[wizardKey]) {
                merged[wizardKey] = wizardVal;
              }
            }
            const buList = data.fields_collected.procurement_bus;
            if (buList && !prev.procurement_bu) {
              merged.procurement_bu = Array.isArray(buList) ? buList[0] : buList;
            }
            const buScalar = data.fields_collected.procurement_bu;
            if (buScalar && typeof buScalar === "string" && !prev.procurement_bu) {
              merged.procurement_bu = buScalar;
            }
            return merged;
          });
        }
      }
      const reply =
        ["ok", "success", "completed"].includes(status)
          ? backendMsg || `Done! ${agent?.label} submitted to Oracle successfully.`
          : ["needs_input", "validating_fields"].includes(status)
            ? backendMsg || "Please fill in the required fields."
            : ["awaiting_confirm", "awaiting_po_decision"].includes(status)
              ? backendMsg || "Please review and confirm the details."
              : status === "llm_error"
                ? backendMsg || "I had trouble parsing that. Could you rephrase?"
                : status === "oracle_error"
                  ? backendMsg || `Oracle returned an error (HTTP ${data?.http_status}).`
                  : status === "unconfigured"
                    ? backendMsg || "Oracle profile isn't set up. Click ⚙ to configure it."
                    : backendMsg || "Something unexpected happened.";

      const now2 = ts();
      setMessages((prev) =>
        prev.map((m) =>
          m.loading
            ? {
              ...m,
              loading: false,
              text: reply,
              result: data,
              timestamp: now2,
              onSave:
                ["ok", "success"].includes(status) && data?.parsed_payload
                  ? () => saveQuery(data, userText)
                  : null,
            }
            : m
        )
      );

      const replyLower = (reply || "").toLowerCase();
      const widgetType = data?.next_field_ui?.uiType;
      const attachmentWidgetShowing = widgetType === "invoice_attachment_upload";
      const isPOQuestion = (
        status === "awaiting_po_decision"
        || replyLower.includes("do you have a po number")
        || replyLower.includes("do you have a purchase order")
        || replyLower.includes("po number for this invoice")
        || replyLower.includes("provide a po number")
        || replyLower.includes("standalone invoice")
        || (replyLower.includes("po") && replyLower.includes("standalone"))
      );

      if (isPOQuestion && !poDecisionMade && !attachmentWidgetShowing) {
        setShowPOCard(true);
      }

      const isConfirmQuestion = (
        status === "awaiting_confirm"
        || (replyLower.includes("reply confirm") && replyLower.includes("oracle"))
        || (replyLower.includes("confirm") && replyLower.includes("create this in oracle"))
        || (replyLower.includes("confirm to create"))
      );

      if (isConfirmQuestion && !isSupplierChat && !["ok", "success", "completed", "failed"].includes(status)) {
        setPendingConfirmText(reply);
        setShowConfirmPopup(true);
      }

      await loadSaved(openAgent);
    } catch (e) {
      setMessages((prev) =>
        prev.map((m) =>
          m.loading
            ? { ...m, loading: false, timestamp: ts(), text: `Error: ${e?.response?.data?.detail || e.message}` }
            : m
        )
      );
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const saveQuery = async (result, prompt) => {
    const label = window.prompt(
      "Name this saved query:",
      `${agent?.label} · ${new Date().toISOString().slice(0, 10)}`
    );
    if (!label) return;
    setSavedBusy(true);
    try {
      await api.post(`/erp-agents/saved-queries`, {
        agent_key: openAgent,
        label: label.trim(),
        prompt,
        parsed_payload: result.parsed_payload,
      });
      await loadSaved(openAgent);
      setMessages((prev) => [
        ...prev,
        { id: Date.now(), role: "assistant", timestamp: ts(), text: `Saved as "${label.trim()}".` },
      ]);
    } catch (e) {
      window.alert(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSavedBusy(false);
    }
  };

  const replay = async (sid) => {
    setBusy(true);
    if (!chatStarted) setChatStarted(true);
    setMessages((prev) => [
      ...prev,
      { id: Date.now() - 1, role: "user", text: "↩ Replaying saved query…", timestamp: ts() },
      { id: Date.now(), role: "assistant", loading: true },
    ]);
    try {
      const { data } = await api.post(`/erp-agents/saved-queries/${sid}/replay`);
      setMessages((prev) =>
        prev.map((m) =>
          m.loading
            ? {
              ...m,
              loading: false,
              result: data,
              timestamp: ts(),
              text: data?.status === "ok" ? "Replayed successfully!" : `Replay error: ${data?.status}`,
            }
            : m
        )
      );
      await loadSaved(openAgent);
    } catch (e) {
      setMessages((prev) =>
        prev.map((m) =>
          m.loading ? { ...m, loading: false, timestamp: ts(), text: `Replay failed: ${e.message}` } : m
        )
      );
    } finally {
      setBusy(false);
    }
  };

  const removeSaved = async (sid) => {
    if (!window.confirm("Delete?")) return;
    try {
      await api.delete(`/erp-agents/saved-queries/${sid}`);
      await loadSaved(openAgent);
    } catch (e) { window.alert(e?.response?.data?.detail || e.message); }
  };

  const createSchedule = async (sqid) => {
    try {
      await api.post(`/erp-agents/schedules`, { saved_query_id: sqid, ...schedForm });
      setOpenSchedFor(null);
      setSchedForm({ cadence: "monthly", day_of_week: 0, day_of_month: 1, hour_utc: 8 });
      await loadSaved(openAgent);
    } catch (e) { window.alert(e?.response?.data?.detail || e.message); }
  };

  const toggleSchedule = async (id) => {
    try {
      await api.post(`/erp-agents/schedules/${id}/toggle`);
      await loadSaved(openAgent);
    } catch (e) { window.alert(e?.response?.data?.detail || e.message); }
  };

  const deleteSchedule = async (id) => {
    if (!window.confirm("Delete schedule?")) return;
    try {
      await api.delete(`/erp-agents/schedules/${id}`);
      await loadSaved(openAgent);
    } catch (e) { window.alert(e?.response?.data?.detail || e.message); }
  };

  const schedsFor = (sid) => schedules.filter((s) => s.saved_query_id === sid);

  const submitWizard = async (forceRun = false) => {
    setWizardBusy(true);
    setWizardResult(null);
    setFieldErrors({});
    setFieldWarnings({});
    try {
      const payload = payloadFV();
      payload.force_run = forceRun;

      if (openAgent === "supplier-create") {
        payload.attachments = await buildAttachmentsPayload();
        delete payload.classification_documents;
      }

      const { data } = await api.post(`/erp-agents/${openAgent}/manual`, payload);
      setWizardResult(data);
      if (data?.field_errors) setFieldErrors(data.field_errors);
      if (data?.field_warnings) setFieldWarnings(data.field_warnings);
    } catch (e) {
      const rawDetail = e?.response?.data?.detail;
      let detail;
      if (Array.isArray(rawDetail)) {
        detail = rawDetail
          .map((err) => {
            const field = Array.isArray(err.loc) ? err.loc.join(" → ") : String(err.loc || "");
            return field ? `${field}: ${err.msg}` : err.msg;
          })
          .join(" · ");
      } else if (rawDetail && typeof rawDetail === "object") {
        detail = JSON.stringify(rawDetail);
      } else {
        detail = rawDetail || e.message || "Unknown error";
      }
      setWizardResult({ status: "error", message: detail });
      setFieldErrors({ _general: detail });
    } finally {
      setWizardBusy(false);
    }
  };

  const handlePOYes = (poNum) => {
    setShowPOCard(false);
    setPoDecisionMade(true);
    sendMessage(`PO number: ${poNum}`);
  };

  const handlePOSkip = () => {
    setShowPOCard(false);
    setPoDecisionMade(true);
    sendMessage("No PO — create as standalone invoice");
  };

  const handleConfirmInvoice = () => {
    setShowConfirmPopup(false);
    sendMessage("yes");
  };

  const handleCancelInvoice = () => {
    setShowConfirmPopup(false);
    const cancelText = openAgent === "supplier-create"
      ? "Supplier creation cancelled. Let me know if you'd like to make any changes."
      : "Invoice creation cancelled. Let me know if you'd like to make any changes.";
    setMessages((prev) => [
      ...prev,
      { id: Date.now(), role: "assistant", timestamp: ts(), text: cancelText },
    ]);
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
  };

  const startNewChat = async () => {
    let newId = crypto.randomUUID();
    try {
      const { data } = await api.post(`/erp-agents/${openAgent}/sessions/new`);
      if (data?.session_id) newId = data.session_id;
    } catch { /* fall back to the local uuid */ }
    setSessionId(newId);
    setMessages([]);
    setInput("");
    setChatStarted(false);
    setShowPOCard(false);
    setPoDecisionMade(false);
    setShowConfirmPopup(false);
    setPendingConfirmText("");
    setFieldValues({});
    setInvoicePendingFiles([]);
    pendingAttachmentModeRef.current = null;
  };

  const openSession = async (sid) => {
    try {
      const { data } = await api.get(`/erp-agents/${openAgent}/sessions/${sid}/messages`);
      const loaded = (data?.messages || []).map((m, i) => ({
        id: Date.now() + i,
        role: m.role,
        text: m.content,
        timestamp: m.created_at
          ? new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
          : ts(),
        result: m.next_field_ui ? { next_field_ui: m.next_field_ui } : undefined,
      }));
      setSessionId(sid);
      setMessages(loaded);
      setChatStarted(true);
      setPoDecisionMade(true);
      setShowPOCard(false);
      setShowConfirmPopup(false);
    } catch (e) {
      window.alert(`Could not open conversation: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  };

  const savedProps = {
    saved, schedsFor, busy, openSchedFor, setOpenSchedFor,
    schedForm, setSchedForm, replay, removeSaved, createSchedule, toggleSchedule, deleteSchedule,
  };

  const isChatOnlyWizard = isChatOnlyAgent(openAgent);

  const inputAttachProps = {
    onAttach:
      isSupplierChat ? handleAttachFiles
      : openAgent === "receipt-create" ? handleReceiptAttachFiles
      : undefined,
    attaching:
      isSupplierChat ? attaching
      : receiptAttaching,
    attachedCount:
      isSupplierChat ? attachedCount
      : receiptAttachedName ? 1 : 0,
  };

  return (
    <>
      <style>{`
        @keyframes chatFadeIn    { from { opacity:0; transform:translateX(-10px); } to { opacity:1; transform:translateX(0); } }
        @keyframes widgetExpand  { from { opacity:0; transform:translateY(-6px) scaleY(0.96); max-height:0; } to { opacity:1; transform:translateY(0) scaleY(1); max-height:600px; } }
        .widget-expand { animation: widgetExpand 0.32s cubic-bezier(0.22,1,0.36,1); transform-origin: top; overflow: hidden; }
        .link-fade-out { animation: chatFadeIn 0.2s ease reverse; }
        @keyframes wizardSlideIn { from { opacity:0; transform:translateX(20px);  } to { opacity:1; transform:translateX(0); } }
        .modal-pre  { width:672px; max-width:calc(100vw - 2rem); max-height:90vh; }
        .modal-chat {
          width:min(74vw,1120px); height:78vh;
          resize: both; overflow: auto;
          min-width: 640px;  min-height: 460px;
          max-width: 96vw;   max-height: 94vh;
        }
        .modal-chat-wide { width:min(96vw,1600px); height:95vh; }
        .modal-chat-fixed {
          width:min(80vw,1200px); height:82vh;
          resize: both; overflow: hidden;
          min-width: 720px;  min-height: 480px;
          max-width: 96vw;   max-height: 94vh;
        }
        .modal-chat-fixed::-webkit-resizer { display: none; }
        .modal-rfp { width:min(85vw,1250px); height:88vh; max-width:85vw; max-height:88vh; }
        .modal-chat::-webkit-resizer { display: none; }
        .modal-resize-hint {
          position: absolute; right: 3px; bottom: 3px; width: 14px; height: 14px;
          cursor: nwse-resize; pointer-events: none; opacity: 0.5;
        }
      `}</style>

      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => { if (!chatStarted) close(); }}>
        <div
          onClick={(e) => e.stopPropagation()}
          data-testid={`agent-modal-${openAgent}`}
          className={`relative bg-white text-gray-900 border border-gray-300 flex flex-col overflow-hidden
            ${(pdfReview || invoiceUploadBusy) ? "modal-chat-fixed" : chatStarted ? (isChatOnlyWizard ? "modal-chat-wide" : "modal-chat") : "modal-pre overflow-hidden"}`}
          style={{ transition: chatStarted ? "none" : "width 0.45s cubic-bezier(0.4,0,0.2,1), height 0.45s cubic-bezier(0.4,0,0.2,1)" }}
        >
          {(chatStarted || pdfReview || invoiceUploadBusy) && (
            <svg className="modal-resize-hint" viewBox="0 0 14 14" fill="none">
              <path d="M13 5 L5 13 M13 9 L9 13 M13 1 L1 13" stroke="#9CA3AF" strokeWidth="1.2" />
            </svg>
          )}
          <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between shrink-0">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">
                Oracle Fusion ERP · Vaku AI
              </div>
              <h3 className="text-2xl font-black tracking-tight mt-0.5">{agent?.label}</h3>
              <p className="text-xs text-gray-500 mt-0.5">{agent?.hint}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {supportsHistory && chatStarted && (
                <>
                  <ChatHistoryControl
                    agentKey={openAgent}
                    currentSessionId={sessionId}
                    onNewChat={startNewChat}
                    onOpenSession={openSession}
                  />
                  <div className="w-px h-5 bg-gray-200" />
                </>
              )}
              <button
                onClick={() => { close(); setSetupOpen(true); }}
                data-testid="agent-open-profile"
                className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1">
                <Gear size={13} weight="bold" /> {profile?.oracle_username || "Set up profile"}
              </button>
              <button onClick={close} data-testid="agent-modal-close">
                <X size={18} />
              </button>
            </div>
          </div>

          {isInvoiceChat && invoiceUploadBusy ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-4 p-10">
              <div className="w-20 h-20 rounded-full bg-[#0020C2]/10 flex items-center justify-center">
                <span className="w-10 h-10 border-[3px] border-[#0020C2]/25 border-t-[#0020C2] rounded-full animate-spin" />
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-gray-900">Reading your invoice…</div>
                <div className="text-sm text-gray-500 mt-1">
                  Extracting supplier, amounts, and line items from the PDF.
                </div>
              </div>
            </div>
          ) : !chatStarted ? (
            <>
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                {isInvoiceChat && (
                  <label
                    className={`block rounded-2xl border-2 bg-[#0020C2]/[0.03] p-6
                      flex flex-col items-center text-center gap-3 transition-all
                      ${invoiceUploadBusy
                        ? "border-[#0020C2]/40 opacity-70 cursor-wait"
                        : "border-[#0020C2]/25 hover:border-[#0020C2] hover:bg-[#0020C2]/[0.06] cursor-pointer"}`}>
                    <input
                      type="file"
                      accept=".pdf"
                      className="hidden"
                      disabled={invoiceUploadBusy}
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        e.target.value = "";
                        if (files.length) handleInvoicePdfUpload(files);
                      }}
                    />
                    <div className="w-14 h-14 rounded-full bg-[#0020C2]/10 flex items-center justify-center">
                      {invoiceUploadBusy
                        ? <span className="w-6 h-6 border-2 border-[#0020C2]/30 border-t-[#0020C2] rounded-full animate-spin" />
                        : <Paperclip size={22} weight="bold" className="text-[#0020C2]" />}
                    </div>
                    <div>
                      <div className="text-sm font-bold text-gray-900">
                        {invoiceUploadBusy ? "Reading PDF…" : "Upload PDF"}
                      </div>
                      <div className="text-xs text-gray-500 mt-1 leading-relaxed">
                        Drop an invoice PDF and we'll extract the details
                      </div>
                    </div>
                  </label>
                )}

                {isInvoiceChat && invoiceUploadMsg && (
                  <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    {invoiceUploadMsg}
                  </div>
                )}

                <SavedQueries {...savedProps} />
                {!hideWizard && (
                  <WizardFields
                    agentKey={openAgent}
                    values={fieldValues}
                    onChange={setFieldValues}
                    missingFields={missingFields}
                    onCreateNew={(key) => { close(); setTimeout(() => launchAgent(key), 100); }}
                    fieldErrors={fieldErrors}
                    fieldWarnings={fieldWarnings}
                    onSchemaLoad={setWizardSchemaFields}
                  />
                )}

                {wizardResult && (
                  <div className={`border px-3 py-2 text-xs flex items-start gap-2
                    ${["ok", "success"].includes((wizardResult.status || "").toLowerCase())
                      ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                      : "border-[#DC2626] bg-red-50 text-[#DC2626]"}`}>
                    {["ok", "success"].includes((wizardResult.status || "").toLowerCase())
                      ? <CheckCircle size={13} weight="fill" className="mt-0.5 shrink-0" />
                      : <WarningCircle size={13} weight="fill" className="mt-0.5 shrink-0" />}
                    <span className="font-bold">
                      {typeof wizardResult.message === "string"
                        ? wizardResult.message
                        : JSON.stringify(wizardResult.message)}
                    </span>
                    {wizardResult.invoice_number && (
                      <span className="font-mono ml-1 opacity-70">#{wizardResult.invoice_number}</span>
                    )}
                  </div>
                )}

                {Object.keys(fieldErrors).length > 0 && (
                  <div className="border border-[#DC2626] bg-red-50 px-3 py-2 text-xs text-[#DC2626] flex items-start gap-2">
                    <WarningCircle size={13} weight="fill" className="mt-0.5 shrink-0" />
                    <span className="font-bold">
                      {Object.keys(fieldErrors).length} error(s) above — fix or submit anyway.
                    </span>
                  </div>
                )}
              </div>

              {!isInvoiceChat && (
                <div className="border-t border-gray-200 px-4 py-2 bg-white shrink-0">
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <Robot size={12} weight="bold" />
                    <span>Type a message below to start the chat</span>
                  </div>
                </div>
              )}

              <InputBar
                inputRef={inputRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onSend={() => sendMessage(input)}
                busy={busy}
                placeholder={agent?.hint || "Type a message to chat with AI…"}
              />

              {!hideWizard && (
                <div className="shrink-0 border-t border-gray-200 px-5 py-3 bg-gray-50 flex flex-wrap gap-2">
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); submitWizard(false); }}
                    disabled={wizardBusy}
                    data-testid="agent-run"
                    className="bg-[#0020C2] text-white px-4 py-2.5 text-xs font-bold uppercase tracking-wider
                      hover:bg-[#001AA3] disabled:opacity-50 flex items-center gap-2">
                    {wizardBusy
                      ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      : <PaperPlaneRight size={13} weight="fill" />}
                    {wizardBusy ? "Sending…" : "Send to Oracle Fusion"}
                  </button>
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); dryRunValidate(); }}
                    disabled={wizardBusy || validating}
                    data-testid="agent-validate"
                    className="border border-[#0020C2] text-[#0020C2] hover:bg-[#0020C2] hover:text-white
                      px-4 py-2.5 text-xs font-bold uppercase tracking-wider disabled:opacity-50">
                    {validating ? "Validating…" : "Dry-run validate"}
                  </button>
                  {Object.keys(fieldErrors).length > 0 && (
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); submitWizard(true); }}
                      disabled={wizardBusy}
                      data-testid="agent-submit-anyway"
                      className="border border-[#DC2626] text-[#DC2626] hover:bg-red-50
                        px-4 py-2.5 text-xs font-bold uppercase tracking-wider disabled:opacity-50">
                      Submit anyway
                    </button>
                  )}
                </div>
              )}
            </>

          ) : (

            <div className="flex flex-1 overflow-hidden">

              <div
                className={`flex flex-col overflow-hidden ${hideWizard ? "" : "border-r border-gray-200"}`}
                style={{ width: hideWizard ? "100%" : "60%", animation: "chatFadeIn 0.4s cubic-bezier(0.4,0,0.2,1)" }}>
                {isSupplierChat && <ProgressBar progress={copilotProgress} />}
                {isSupplierChat && <CapturedFieldsPanel fields={copilotFields} />}
                {isSupplierChat && messages.length <= 1 && (
                  <QuickActions onPick={(text) => sendMessage(text)} />
                )}
                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50">
                  {messages.map((msg) => (
                    <ChatBubble
                      key={msg.id}
                      msg={msg}
                      sessionId={sessionId}
                      onOptionClick={(opt) => sendMessage(opt.value)}
                      onMultiSubmit={(values) => sendMessage(values.join(", "))}
                      isSupplierChat={isSupplierChat}
                      onInvoiceAttach={handleInvoiceWidgetUpload}
                      onInvoiceSkipAttach={handleInvoiceWidgetSkip}
                    />
                  ))}

                  {showPOCard && !poDecisionMade && (
                    <PODecisionCard onYes={handlePOYes} onSkip={handlePOSkip} />
                  )}

                  <div ref={messagesEndRef} />
                </div>
                <InputBar
                  inputRef={inputRef}
                  value={input}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  onSend={() => sendMessage(input)}
                  busy={busy}
                  placeholder="Continue the conversation…"
                  onAttach={inputAttachProps.onAttach}
                  attaching={inputAttachProps.attaching}
                  attachedCount={inputAttachProps.attachedCount}
                />
              </div>

              {!hideWizard && (
                <div
                  className="flex flex-col bg-white"
                  style={{ width: "40%", minWidth: "360px", animation: "wizardSlideIn 0.45s cubic-bezier(0.4,0,0.2,1)" }}>

                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] flex items-center gap-1.5">
                      <Sparkle size={11} weight="fill" /> Wizard fields
                      <span className="text-gray-400 normal-case font-normal tracking-normal ml-1">
                        · auto-filled as you chat
                      </span>
                    </div>

                    <SavedQueries {...savedProps} />

                    <WizardFields
                      agentKey={openAgent}
                      values={fieldValues}
                      onChange={setFieldValues}
                      missingFields={missingFields}
                      onCreateNew={(key) => { close(); setTimeout(() => launchAgent(key), 100); }}
                      fieldErrors={fieldErrors}
                      fieldWarnings={fieldWarnings}
                      onSchemaLoad={setWizardSchemaFields}
                    />

                    {Object.keys(fieldErrors).length > 0 && (
                      <div className="border border-[#DC2626] bg-red-50 px-3 py-2 text-xs text-[#DC2626] flex items-start gap-2">
                        <WarningCircle size={13} weight="fill" className="mt-0.5 shrink-0" />
                        <span className="font-bold">
                          {Object.keys(fieldErrors).length} error(s) — fix above.
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="shrink-0 border-t border-gray-200 p-4 space-y-2 bg-white">
                    {wizardResult && (
                      <div className={`border px-3 py-2 text-xs flex items-start gap-2 rounded
                        ${["ok", "success"].includes((wizardResult.status || "").toLowerCase())
                          ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                          : "border-[#DC2626] bg-red-50 text-[#DC2626]"}`}>
                        {["ok", "success"].includes((wizardResult.status || "").toLowerCase())
                          ? <CheckCircle size={12} weight="fill" className="mt-0.5 shrink-0" />
                          : <WarningCircle size={12} weight="fill" className="mt-0.5 shrink-0" />}
                        <span className="font-bold text-[11px]">
                          {typeof wizardResult.message === "string"
                            ? wizardResult.message
                            : JSON.stringify(wizardResult.message)}
                        </span>
                        {wizardResult.invoice_number && (
                          <span className="font-mono ml-1 opacity-70 text-[10px]">#{wizardResult.invoice_number}</span>
                        )}
                      </div>
                    )}

                    <button
                      onClick={() => submitWizard(false)}
                      disabled={wizardBusy}
                      data-testid="agent-run"
                      className="w-full bg-[#0020C2] text-white px-4 py-3 text-xs font-bold uppercase tracking-wider
                        hover:bg-[#001AA3] disabled:opacity-50 flex items-center justify-center gap-2">
                      {wizardBusy
                        ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        : <PaperPlaneRight size={13} weight="fill" />}
                      {wizardBusy ? "Sending…" : "Send to Oracle Fusion"}
                    </button>
                    <button
                      onClick={dryRunValidate}
                      disabled={wizardBusy || validating}
                      data-testid="agent-validate"
                      className="w-full border border-[#0020C2] text-[#0020C2] hover:bg-[#0020C2] hover:text-white
                        px-4 py-2.5 text-xs font-bold uppercase tracking-wider disabled:opacity-50
                        flex items-center justify-center gap-2">
                      {validating ? "Validating…" : "Dry-run validate"}
                    </button>
                    {Object.keys(fieldErrors).length > 0 && (
                      <button
                        onClick={() => submitWizard(true)}
                        disabled={wizardBusy}
                        data-testid="agent-submit-anyway"
                        className="w-full border border-[#DC2626] text-[#DC2626] hover:bg-red-50
                          px-4 py-2.5 text-xs font-bold uppercase tracking-wider disabled:opacity-50
                          flex items-center justify-center">
                        Submit anyway
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <PdfInvoiceReview
            open={!!pdfReview}
            sessionId={sessionId}
            extracted={pdfReview}
            pdfUrl={pdfFileUrl}
            onClose={() => setPdfReview(null)}
            onCreated={(result) => {
              setPdfReview(null);
          setPdfFileUrl((u) => { if (u) { try { URL.revokeObjectURL(u); } catch {} } return null; });
              setChatStarted(true);
              setMessages([
                {
                  id: Date.now(),
                  role: "assistant",
                  text: result?.reply || result?.response ||
                    `Invoice created in Oracle${result?.invoice_id ? ` — ID ${result.invoice_id}` : ""}.`,
                  result,
                  timestamp: ts(),
                },
              ]);
            }}
          />
        </div>

      </div>

      {showConfirmPopup && (
        <InvoiceConfirmPopup
          text={pendingConfirmText}
          onConfirm={handleConfirmInvoice}
          onCancel={handleCancelInvoice}
          isSupplier={openAgent === "supplier-create"}
        />
      )}
    </>
  );
}